// web/src/scoring/webScorer.ts
// FRAME Scoring Engine v2 — Real CV, full-range, memory-safe
// Replaces the broken v1 that produced clustered 5.5-7.0 scores
// Patent refs: Provisionals 1-7 (Feb-Apr 2026), Non-provisional claims 1-25

import type { PhotoScore, PhotoCategory, WeightProfile, DimensionScores, PrismAestheticProperties, PrismPreferenceVector, PrismOwnerBaseline, NovelDimensionScores, DepthCues, AuthenticitySignals, NovelWeightProfile, GazeResult } from '@frame/scoring-types';
import { NOVEL_MODE_WEIGHTS, LandmarkTier } from '@frame/scoring-types';
import { computeComposite, computeCrowdPenalty, percentileLabel, extractAestheticProperties, applyPrismModifier, analyzeColorHarmony, applyHarmonyModeWeight, analyzeDepth, analyzeAuthenticity, analyzeEmotionalResonance, analyzeGazeDynamics, analyzeSubjectEnvironment, computeNovelComposite, blendComposites, scoreSocialCalibration, computeSocialCalibrationComposite, blendWithSocialCalibration, applyLandmarkSpike, transformScore, computeGlowScore } from '@frame/utils';
import { detectLandmarkFromGPS } from './landmarks';
import type { ScoringIntermediates, SocialSignals } from '@frame/utils';

// ─── Debug overlay: exposes intermediate scoring values for verification ──
export interface ScoringDebug {
  rawDimensions: { lighting: number; composition: number; sceneInterest: number; portrait: number | null };
  calibratedDimensions: { lighting: number; composition: number; sceneInterest: number; portrait: number | null };
  effectiveWeights: Record<string, number>;
  detractorChain: Array<{ type: string; severity: number; remaining: number }>;
  novelInputs: { depthCues: Record<string, number>; authenticitySignals: Record<string, number>; colorTemp: string };
  exifBonuses: { bonus: number; notes: string[] };
  modeSpecificBonuses: Array<{ type: string; value: number }>;
}

// ═══════════════════════════════════════════════════════════════
// SECTION 1: TF.js + MediaPipe Model Loading (unchanged, works)
// ═══════════════════════════════════════════════════════════════

let tf: any = null;
let nimaModel: any = null;
let nimaLoading = false;

async function loadTF() {
  if (tf) return tf;
  try {
    tf = await import('@tensorflow/tfjs');
    if (typeof navigator !== 'undefined' && 'gpu' in navigator) {
      try { await tf.setBackend('webgpu'); } catch { await tf.setBackend('webgl'); }
    } else {
      await tf.setBackend('webgl');
    }
    await tf.ready();
    return tf;
  } catch (err) {
    console.warn('[FRAME] TF.js unavailable:', err);
    return null;
  }
}

async function loadNIMA(): Promise<boolean> {
  if (nimaModel) return true;
  if (nimaLoading) {
    while (nimaLoading) await new Promise(r => setTimeout(r, 100));
    return !!nimaModel;
  }
  nimaLoading = true;
  try {
    const tfjs = await loadTF();
    if (!tfjs) return false;
    nimaModel = await tfjs.loadGraphModel('/models/nima/model.json');
    return true;
  } catch {
    console.warn('[FRAME] NIMA not loaded — canvas-only scoring active');
    return false;
  } finally {
    nimaLoading = false;
  }
}

async function scoreWithNIMA(canvas: HTMLCanvasElement): Promise<number> {
  if (!nimaModel || !tf) return -1;
  try {
    return tf.tidy(() => {
      const small = document.createElement('canvas');
      small.width = 224; small.height = 224;
      small.getContext('2d')!.drawImage(canvas, 0, 0, 224, 224);
      const tensor = tf.browser.fromPixels(small)
        .toFloat().div(127.5).sub(1.0).expandDims(0);
      const output = nimaModel.predict(tensor);
      const dist = Array.from(output.dataSync()) as number[];
      small.width = 1; small.height = 1; // cleanup
      let mean = 0;
      for (let i = 0; i < dist.length; i++) mean += (i + 1) * dist[i];
      return mean;
    });
  } catch { return -1; }
}

let faceLandmarker: any = null;

async function loadFaceLandmarker() {
  if (faceLandmarker) return faceLandmarker;
  try {
    const vision = await import('@mediapipe/tasks-vision');
    const { FaceLandmarker, FilesetResolver } = vision;
    const resolver = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm'
    );
    faceLandmarker = await FaceLandmarker.createFromOptions(resolver, {
      baseOptions: {
        modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task',
        delegate: 'GPU',
      },
      runningMode: 'IMAGE',
      numFaces: 8,
      outputFaceBlendshapes: true,
    });
    return faceLandmarker;
  } catch (err) {
    console.warn('[FRAME] MediaPipe unavailable:', err);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════
// SECTION 2: Screenshot Detection (Provisional 7, Claim 24)
// 5 independent signals — need 3+ to trigger gate
// ═══════════════════════════════════════════════════════════════

function detectScreenshot(data: Uint8ClampedArray, width: number, height: number): number {
  let confidence = 0;

  // Signal 1: Flat color blocks (UI backgrounds have near-zero variance)
  const blockSize = 16;
  let flatBlocks = 0, totalBlocks = 0;
  for (let by = 0; by < height - blockSize; by += blockSize) {
    for (let bx = 0; bx < width - blockSize; bx += blockSize) {
      let sum = 0, sumSq = 0, count = 0;
      for (let y = by; y < Math.min(by + blockSize, height); y++) {
        for (let x = bx; x < Math.min(bx + blockSize, width); x++) {
          const idx = (y * width + x) * 4;
          const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
          sum += lum; sumSq += lum * lum; count++;
        }
      }
      if (count > 0 && (sumSq / count) - (sum / count) ** 2 < 3.0) flatBlocks++;
      totalBlocks++;
    }
  }
  if (totalBlocks > 0 && flatBlocks / totalBlocks > 0.25) confidence += 0.25;

  // Signal 2: Phone screenshot aspect ratios (exclude panoramas)
  const ratio = Math.max(width, height) / Math.min(width, height);
  const isPanorama = ratio > 2.5;
  if (!isPanorama && [2.167, 2.170, 2.222, 2.333, 2.165].some(r => Math.abs(ratio - r) < 0.06)) {
    confidence += 0.20;
  }

  // Signal 3: Low unique colors (screenshots use flat palettes)
  const colorSet = new Set<number>();
  for (let i = 0; i < data.length; i += 16) {
    colorSet.add(((data[i] >> 4) << 8) | ((data[i + 1] >> 4) << 4) | (data[i + 2] >> 4));
  }
  if (colorSet.size / 4096 < 0.30) confidence += 0.20;

  // Signal 4: Status bar (top 7% uniform with sharp transition)
  const topH = Math.max(1, Math.round(height * 0.07));
  let topSum = 0, topSumSq = 0, topCount = 0;
  for (let y = 0; y < topH; y++) {
    for (let x = 0; x < width; x += 2) {
      const idx = (y * width + x) * 4;
      const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
      topSum += lum; topSumSq += lum * lum; topCount++;
    }
  }
  if (topCount > 0) {
    const topVar = (topSumSq / topCount) - (topSum / topCount) ** 2;
    if (topVar < 50) confidence += 0.15;
  }

  // Signal 5: Sensor noise absence (photos have noise std>3, screenshots std<1.5)
  const sx = Math.floor(width * 0.4), sy = Math.floor(height * 0.4);
  const sz = Math.min(32, Math.floor(width * 0.1), Math.floor(height * 0.1));
  let nSum = 0, nSumSq = 0, nCount = 0;
  for (let y = sy; y < sy + sz && y < height; y++) {
    for (let x = sx; x < sx + sz && x < width; x++) {
      const idx = (y * width + x) * 4;
      const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
      nSum += lum; nSumSq += lum * lum; nCount++;
    }
  }
  if (nCount > 0) {
    const noiseStd = Math.sqrt(Math.max(0, (nSumSq / nCount) - (nSum / nCount) ** 2));
    if (noiseStd < 1.5) confidence += 0.20;
  }

  return Math.min(1.0, confidence);
}

// ═══════════════════════════════════════════════════════════════
// SECTION 2b: Non-Photo Content Gate (cartoons, memes, graphics)
// Expands screenshot detection to catch ALL non-photographic content
// ═══════════════════════════════════════════════════════════════

interface NonPhotoResult {
  isNonPhoto: boolean;
  confidence: number;
  category: 'screenshot' | 'cartoon' | 'meme' | 'illustration' | 'photo';
  reason: string;
}

function countQuantizedColors(data: Uint8ClampedArray, bits: number): number {
  const shift = 8 - bits;
  const colors = new Set<number>();
  const step = 8;
  for (let i = 0; i < data.length; i += step * 4) {
    const r = data[i] >> shift;
    const g = data[i + 1] >> shift;
    const b = data[i + 2] >> shift;
    colors.add((r << (bits * 2)) | (g << bits) | b);
  }
  return colors.size;
}

function computeFlatColorRatio(data: Uint8ClampedArray): number {
  const tolerance = 12;
  const step = 4;
  let flatCount = 0;
  const total = Math.floor(data.length / (step * 4)) - 1;
  if (total <= 0) return 0;
  for (let i = 0; i < data.length - step * 4; i += step * 4) {
    const dr = Math.abs(data[i] - data[i + step * 4]);
    const dg = Math.abs(data[i + 1] - data[i + step * 4 + 1]);
    const db = Math.abs(data[i + 2] - data[i + step * 4 + 2]);
    if (dr < tolerance && dg < tolerance && db < tolerance) flatCount++;
  }
  return flatCount / total;
}

function estimateTextDensity(data: Uint8ClampedArray, w: number, h: number): number {
  let totalTransitions = 0;
  const sampleRows = Math.min(100, h);
  const rowStep = Math.max(1, Math.floor(h / sampleRows));
  let rowCount = 0;
  for (let y = 0; y < h; y += rowStep) {
    let transitions = 0;
    for (let x = 1; x < w; x++) {
      const i = (y * w + x) * 4;
      const iPrev = (y * w + x - 1) * 4;
      const diff = Math.abs(data[i] - data[iPrev]);
      if (diff > 40) transitions++;
    }
    totalTransitions += transitions / w;
    rowCount++;
  }
  return rowCount > 0 ? totalTransitions / rowCount : 0;
}

function detectOutlineRendering(data: Uint8ClampedArray, w: number, h: number): number {
  let outlinePixels = 0;
  let totalSampled = 0;
  const step = 2;
  for (let y = 1; y < h - 1; y += step) {
    for (let x = 1; x < w - 1; x += step) {
      const i = (y * w + x) * 4;
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      totalSampled++;
      if (lum < 50) {
        const iRight = (y * w + x + 1) * 4;
        const lumRight = data[iRight] * 0.299 + data[iRight + 1] * 0.587 + data[iRight + 2] * 0.114;
        if (lumRight > 100) outlinePixels++;
      }
    }
  }
  return totalSampled > 0 ? outlinePixels / totalSampled : 0;
}

function detectNonPhotographicContent(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  pa: PixelAnalysis
): NonPhotoResult {
  const signals: { type: string; weight: number }[] = [];

  // Signal 1: Existing screenshot confidence (reuse 5-signal detector)
  if (pa.screenshotConfidence > 0.6) {
    signals.push({ type: 'screenshot_signals', weight: 3 });
  } else if (pa.screenshotConfidence > 0.3) {
    signals.push({ type: 'screenshot_signals', weight: 1 });
  }

  // Signal 2: Flat color region ratio (cartoons/graphics have large uniform areas)
  const flatRatio = computeFlatColorRatio(data);
  if (flatRatio > 0.85) {
    signals.push({ type: 'flat_regions', weight: 3 });
  } else if (flatRatio > 0.78) {
    signals.push({ type: 'flat_regions', weight: 1 });
  }

  // Signal 3: Cartoon outline rendering (dark outlines around flat fills)
  const outlineRatio = detectOutlineRendering(data, w, h);
  if (outlineRatio > 0.10) {
    signals.push({ type: 'cartoon_outlines', weight: 3 });
  } else if (outlineRatio > 0.06) {
    signals.push({ type: 'cartoon_outlines', weight: 1 });
  }

  // Signal 4: Low quantized color count (photos 800+, cartoons 20-200)
  const uniqueColors = countQuantizedColors(data, 6);
  if (uniqueColors < 50) {
    signals.push({ type: 'low_color_count', weight: 3 });
  } else if (uniqueColors < 120) {
    signals.push({ type: 'low_color_count', weight: 1 });
  }

  // Signal 5: Text density (screenshots/memes have dense text)
  const textDensity = estimateTextDensity(data, w, h);
  if (textDensity > 0.15) {
    signals.push({ type: 'text_heavy', weight: 2 });
  }

  // Signal 6: Edge sharpness via Sobel (reuse pa.sobelMag)
  // Razor edges: high proportion of max-strength edges = artificial
  // Orthogonal dominance: edges at 0°/90° = UI elements
  let strongEdges = 0, razorEdges = 0, orthogonal = 0;
  for (let i = 0; i < pa.sobelMag.length; i++) {
    if (pa.sobelMag[i] > 30) {
      strongEdges++;
      if (pa.sobelMag[i] > 200) razorEdges++;
      const deg = pa.sobelAngle[i] * (180 / Math.PI);
      const absDeg = Math.abs(deg);
      if (absDeg < 5 || absDeg > 85 && absDeg < 95 || absDeg > 175) orthogonal++;
    }
  }
  if (strongEdges > 0) {
    if (razorEdges / strongEdges > 0.15) {
      signals.push({ type: 'razor_edges', weight: 2 });
    }
    if (orthogonal / strongEdges > 0.55) {
      signals.push({ type: 'orthogonal_edges', weight: 2 });
    }
  }

  // Scoring
  const totalWeight = signals.reduce((sum, s) => sum + s.weight, 0);

  const strongSignals = signals.filter(s => s.weight >= 3).length;
  const isNonPhotoDetected = totalWeight >= 7 || (strongSignals >= 2 && totalWeight >= 5);
  let category: NonPhotoResult['category'] = 'photo';
  if (isNonPhotoDetected) {
    const hasOutlines = signals.some(s => s.type === 'cartoon_outlines');
    const hasScreenshot = signals.some(s => s.type === 'screenshot_signals' && s.weight >= 3);
    const hasText = signals.some(s => s.type === 'text_heavy');
    const hasOrthogonal = signals.some(s => s.type === 'orthogonal_edges');

    if (hasOutlines && flatRatio > 0.65) category = 'cartoon';
    else if (hasScreenshot || (hasOrthogonal && flatRatio > 0.70)) category = 'screenshot';
    else if (hasText && flatRatio > 0.60) category = 'meme';
    else if (signals.length >= 3) category = 'illustration'; // Need 3+ distinct signals
    // else: stays 'photo' — not enough evidence for non-photo classification
  }

  return {
    isNonPhoto: isNonPhotoDetected,
    confidence: Math.min(1.0, totalWeight / 8),
    category,
    reason: signals.map(s => s.type).join(', '),
  };
}

// ═══════════════════════════════════════════════════════════════
// SECTION 2b-2: Photo Tag Detection — descriptive content tags
// ═══════════════════════════════════════════════════════════════

type PhotoTag = 'selfie' | 'group' | 'couple' | 'landscape' | 'sunset' | 'sport' | 'architecture' | 'food' | 'portrait' | 'night' | 'beach' | 'urban' | 'nature' | 'event' | 'macro' | 'pet';

function detectPhotoTags(
  faceResult: { faceCount: number; isSelfie: boolean; largestFaceRatio: number },
  pa: PixelAnalysis,
  lightingResult: { isGoldenHour: boolean; kelvin: number; warmthRatio: number },
  width: number,
  height: number,
): PhotoTag[] {
  const tags: PhotoTag[] = [];

  // ── Face-based tags ──
  if (faceResult.isSelfie) tags.push('selfie');
  else if (faceResult.faceCount === 1) tags.push('portrait');
  if (faceResult.faceCount === 2) tags.push('couple');
  if (faceResult.faceCount >= 3) tags.push('group');
  if (faceResult.faceCount >= 5) tags.push('event');

  // ── Scene-based tags (no faces or alongside face tags) ──
  // Compute edge stats for scene classification
  let strongEdges = 0, orthogonalEdges = 0, edgeSum = 0;
  for (let i = 0; i < pa.sobelMag.length; i++) {
    edgeSum += pa.sobelMag[i];
    if (pa.sobelMag[i] > 30) {
      strongEdges++;
      const deg = pa.sobelAngle[i] * (180 / Math.PI);
      const absDeg = Math.abs(deg);
      if (absDeg < 8 || (absDeg > 82 && absDeg < 98) || absDeg > 172) orthogonalEdges++;
    }
  }
  const orthogonalRatio = strongEdges > 100 ? orthogonalEdges / strongEdges : 0;
  const avgLum = pa.histogram.reduce((sum, count, i) => sum + count * i, 0) / pa.pixelCount;
  const aspectRatio = width / height;

  // Golden hour / sunset
  if (lightingResult.isGoldenHour && pa.colorfulness > 0.35) tags.push('sunset');

  // Night photography
  if (avgLum < 70 && lightingResult.kelvin < 4500) tags.push('night');

  // Scene tags for no-face photos
  if (faceResult.faceCount === 0) {
    if (orthogonalRatio > 0.40) {
      tags.push('architecture');
      if (pa.colorfulness < 0.3) tags.push('urban');
    } else if (pa.colorfulness > 0.45 && aspectRatio > 1.2) {
      tags.push('landscape');
      tags.push('nature');
    } else if (pa.colorfulness > 0.35 && aspectRatio > 1.0) {
      tags.push('landscape');
    }

    // Beach: high brightness + blue channel dominant + warm tones
    if (avgLum > 150 && lightingResult.warmthRatio < 1.1 && pa.colorfulness > 0.3) {
      tags.push('beach');
    }

    // Food: warm tones + close-up composition (no faces, moderate complexity)
    const edgeRatio = strongEdges / pa.sobelMag.length;
    if (lightingResult.warmthRatio > 1.15 && edgeRatio > 0.08 && edgeRatio < 0.25 && aspectRatio < 1.5 && aspectRatio > 0.7) {
      tags.push('food');
    }

    // Macro: very high detail concentration in center
    if (edgeRatio > 0.15 && pa.colorfulness > 0.3) {
      // Check center vs edge detail ratio
      const centerStart = Math.floor(pa.sobelMag.length * 0.3);
      const centerEnd = Math.floor(pa.sobelMag.length * 0.7);
      let centerDetail = 0, outerDetail = 0;
      for (let i = 0; i < pa.sobelMag.length; i++) {
        if (i >= centerStart && i < centerEnd) centerDetail += pa.sobelMag[i];
        else outerDetail += pa.sobelMag[i];
      }
      const centerRatio = centerDetail / (centerDetail + outerDetail + 1);
      if (centerRatio > 0.55) tags.push('macro');
    }
  }

  // Sport: motion blur detected in any context
  const avgEdge = edgeSum / pa.sobelMag.length;
  if (avgEdge < 10 && pa.sobelMag.length > 0) {
    // Check for directional blur = motion
    const bins = new Float64Array(18);
    let totalStrong = 0;
    for (let i = 0; i < pa.sobelMag.length; i++) {
      if (pa.sobelMag[i] > 20) {
        const deg = ((pa.sobelAngle[i] * 180 / Math.PI) % 180 + 180) % 180;
        const bin = Math.min(17, Math.floor(deg / 10));
        bins[bin]++;
        totalStrong++;
      }
    }
    if (totalStrong > 100) {
      let maxWindow = 0;
      for (let i = 0; i < 18; i++) {
        const w = bins[i] + bins[(i + 1) % 18] + bins[(i + 17) % 18];
        if (w > maxWindow) maxWindow = w;
      }
      if (maxWindow / totalStrong > 0.55 && pa.colorfulness > 0.3) tags.push('sport');
    }
  }

  return tags;
}

// ─── Scene Category Inference (for social calibration) ────────
function inferSceneCategory(tags: PhotoTag[]): string {
  if (tags.includes('landscape') || tags.includes('nature')) return 'outdoor';
  if (tags.includes('architecture') || tags.includes('urban')) return 'city';
  if (tags.includes('food')) return 'restaurant';
  if (tags.includes('event')) return 'event';
  if (tags.includes('beach')) return 'beach';
  if (tags.includes('sport')) return 'sports';
  return 'other';
}

// ═══════════════════════════════════════════════════════════════
// SECTION 2c: Near-Duplicate Detection (Average Hash)
// ═══════════════════════════════════════════════════════════════

function computeAverageHash(data: Uint8ClampedArray, w: number, h: number): bigint {
  // Downsample to 8×8 grayscale and compute average hash
  const size = 8;
  const gray = new Float32Array(size * size);
  let sum = 0;
  for (let gy = 0; gy < size; gy++) {
    for (let gx = 0; gx < size; gx++) {
      const srcX = Math.floor(gx * w / size);
      const srcY = Math.floor(gy * h / size);
      const i = (srcY * w + srcX) * 4;
      const lum = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      gray[gy * size + gx] = lum;
      sum += lum;
    }
  }
  const avg = sum / (size * size);
  let hash = BigInt(0);
  for (let i = 0; i < size * size; i++) {
    if (gray[i] >= avg) hash |= BigInt(1) << BigInt(i);
  }
  return hash;
}

function hammingDistance(a: bigint, b: bigint): number {
  let xor = a ^ b;
  let dist = 0;
  while (xor > BigInt(0)) {
    dist += Number(xor & BigInt(1));
    xor >>= BigInt(1);
  }
  return dist;
}

function detectNearDuplicates(
  scores: { id: string; hash: bigint; compositeScore: number }[],
  threshold: number = 8
): Map<string, string> {
  // Returns map of score.id -> groupId (best photo's id in the group)
  const groups = new Map<string, string>();
  const assigned = new Set<string>();

  // Scores are already sorted by compositeScore desc
  for (let i = 0; i < scores.length; i++) {
    if (assigned.has(scores[i].id)) continue;
    for (let j = i + 1; j < scores.length; j++) {
      if (assigned.has(scores[j].id)) continue;
      if (hammingDistance(scores[i].hash, scores[j].hash) < threshold) {
        // j is a near-duplicate of i (i has higher score since sorted)
        groups.set(scores[j].id, scores[i].id);
        assigned.add(scores[j].id);
      }
    }
  }
  return groups;
}

// ═══════════════════════════════════════════════════════════════
// SECTION 3: Single-Pass Pixel Analysis
// Histogram + Sobel + color stats in ONE iteration
// ═══════════════════════════════════════════════════════════════

interface DominantColor {
  r: number; g: number; b: number;
  h: number; s: number; v: number;
  weight: number;
}

interface PixelAnalysis {
  histogram: Float32Array;
  pixelCount: number;
  avgR: number; avgG: number; avgB: number;
  avgSat: number;
  sobelMag: Float32Array;
  sobelAngle: Float32Array;
  lumArray: Float32Array;
  screenshotConfidence: number;
  highlightClip: number; // Fraction of pixels near pure white (lum > 245)
  shadowClip: number;    // Fraction of pixels near pure black (lum < 10)
  // Phase 1 extensions
  dominantColors: DominantColor[];  // k-means palette (k=5)
  noiseLevel: number;               // Luminance stddev in smooth regions
  colorfulness: number;             // Hasler-Süsstrunk metric, normalized 0-1
  wbDeviation: number;              // Gray-world white balance deviation, 0-1
}

// ── K-means palette extraction — samples every 8th pixel for speed ──
function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const d = max - min;
  const v = max;
  const s = max > 0 ? d / max : 0;
  let h = 0;
  if (d > 0) {
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
    else if (max === g) h = ((b - r) / d + 2) * 60;
    else h = ((r - g) / d + 4) * 60;
  }
  return { h, s, v };
}

function extractDominantColors(data: Uint8ClampedArray, pixelCount: number, k = 5, maxIter = 10): DominantColor[] {
  // Sample every 8th pixel
  const step = 8;
  const samples: Array<[number, number, number]> = [];
  for (let i = 0; i < data.length; i += 4 * step) {
    samples.push([data[i], data[i + 1], data[i + 2]]);
  }
  if (samples.length < k) {
    return samples.map(([r, g, b]) => {
      const hsv = rgbToHsv(r, g, b);
      return { r, g, b, ...hsv, weight: 1 / samples.length };
    });
  }

  // Initialize centroids from evenly spaced samples
  const centroids: Array<[number, number, number]> = [];
  const spacing = Math.floor(samples.length / k);
  for (let i = 0; i < k; i++) centroids.push([...samples[i * spacing]]);

  const assignments = new Int32Array(samples.length);

  for (let iter = 0; iter < maxIter; iter++) {
    // Assign each sample to nearest centroid
    for (let i = 0; i < samples.length; i++) {
      let bestDist = Infinity, bestIdx = 0;
      for (let j = 0; j < k; j++) {
        const dr = samples[i][0] - centroids[j][0];
        const dg = samples[i][1] - centroids[j][1];
        const db = samples[i][2] - centroids[j][2];
        const dist = dr * dr + dg * dg + db * db;
        if (dist < bestDist) { bestDist = dist; bestIdx = j; }
      }
      assignments[i] = bestIdx;
    }
    // Update centroids
    const sums = Array.from({ length: k }, () => [0, 0, 0, 0] as [number, number, number, number]);
    for (let i = 0; i < samples.length; i++) {
      const c = assignments[i];
      sums[c][0] += samples[i][0];
      sums[c][1] += samples[i][1];
      sums[c][2] += samples[i][2];
      sums[c][3]++;
    }
    for (let j = 0; j < k; j++) {
      if (sums[j][3] > 0) {
        centroids[j][0] = sums[j][0] / sums[j][3];
        centroids[j][1] = sums[j][1] / sums[j][3];
        centroids[j][2] = sums[j][2] / sums[j][3];
      }
    }
  }

  // Build result with weights
  const counts = new Float64Array(k);
  for (let i = 0; i < samples.length; i++) counts[assignments[i]]++;
  const total = samples.length;

  return centroids
    .map(([r, g, b], i) => {
      const hsv = rgbToHsv(r, g, b);
      return { r: Math.round(r), g: Math.round(g), b: Math.round(b), ...hsv, weight: counts[i] / total };
    })
    .filter(c => c.weight > 0)
    .sort((a, b) => b.weight - a.weight);
}

function singlePassAnalysis(data: Uint8ClampedArray, width: number, height: number): PixelAnalysis {
  const pixelCount = width * height;
  const histogram = new Float32Array(256);
  const lumArray = new Float32Array(pixelCount);
  let rSum = 0, gSum = 0, bSum = 0, satSum = 0;

  // Hasler-Süsstrunk colorfulness accumulators
  let rgSum = 0, rgSumSq = 0, ybSum = 0, ybSumSq = 0;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const lum = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
    const pixIdx = i / 4;
    histogram[Math.min(255, lum)]++;
    lumArray[pixIdx] = lum;
    rSum += r; gSum += g; bSum += b;
    const maxC = Math.max(r, g, b), minC = Math.min(r, g, b);
    satSum += maxC > 0 ? (maxC - minC) / maxC : 0;

    // Colorfulness opponent channels
    const rg = r - g;
    const yb = 0.5 * (r + g) - b;
    rgSum += rg; rgSumSq += rg * rg;
    ybSum += yb; ybSumSq += yb * yb;
  }

  // Colorfulness (Hasler-Süsstrunk)
  const rgMean = rgSum / pixelCount;
  const ybMean = ybSum / pixelCount;
  const rgStd = Math.sqrt(Math.max(0, rgSumSq / pixelCount - rgMean * rgMean));
  const ybStd = Math.sqrt(Math.max(0, ybSumSq / pixelCount - ybMean * ybMean));
  const colorfulnessRaw = Math.sqrt(rgStd * rgStd + ybStd * ybStd) + 0.3 * Math.sqrt(rgMean * rgMean + ybMean * ybMean);
  const colorfulness = Math.min(1, colorfulnessRaw / 150);

  // White balance deviation (gray-world)
  const avgR = rSum / pixelCount, avgG = gSum / pixelCount, avgB = bSum / pixelCount;
  const maxAvg = Math.max(avgR, avgG, avgB);
  const wbDeviation = maxAvg > 0
    ? Math.max(Math.abs(avgR - avgG), Math.abs(avgR - avgB), Math.abs(avgG - avgB)) / maxAvg
    : 0;

  const sobelMag = new Float32Array(pixelCount);
  const sobelAngle = new Float32Array(pixelCount);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = y * width + x;
      const gx = -lumArray[(y-1)*width+(x-1)] + lumArray[(y-1)*width+(x+1)]
               - 2*lumArray[y*width+(x-1)] + 2*lumArray[y*width+(x+1)]
               - lumArray[(y+1)*width+(x-1)] + lumArray[(y+1)*width+(x+1)];
      const gy = -lumArray[(y-1)*width+(x-1)] - 2*lumArray[(y-1)*width+x] - lumArray[(y-1)*width+(x+1)]
               + lumArray[(y+1)*width+(x-1)] + 2*lumArray[(y+1)*width+x] + lumArray[(y+1)*width+(x+1)];
      sobelMag[idx] = Math.sqrt(gx * gx + gy * gy);
      sobelAngle[idx] = Math.atan2(gy, gx);
    }
  }

  // Noise estimation: stddev of luminance in smooth regions (low edge energy)
  let smoothSum = 0, smoothSumSq = 0, smoothCount = 0;
  for (let i = 0; i < pixelCount; i++) {
    if (sobelMag[i] < 5) {
      smoothSum += lumArray[i];
      smoothSumSq += lumArray[i] * lumArray[i];
      smoothCount++;
    }
  }
  const noiseLevel = smoothCount > 100
    ? Math.sqrt(Math.max(0, smoothSumSq / smoothCount - (smoothSum / smoothCount) ** 2))
    : 0;

  const screenshotConfidence = detectScreenshot(data, width, height);

  // Compute highlight/shadow clipping from histogram
  let highlightPixels = 0, shadowPixels = 0;
  for (let i = 245; i < 256; i++) highlightPixels += histogram[i];
  for (let i = 0; i < 10; i++) shadowPixels += histogram[i];
  const highlightClip = pixelCount > 0 ? highlightPixels / pixelCount : 0;
  const shadowClip = pixelCount > 0 ? shadowPixels / pixelCount : 0;

  // K-means dominant color palette
  const dominantColors = extractDominantColors(data, pixelCount);

  return {
    histogram, pixelCount, lumArray,
    avgR, avgG, avgB,
    avgSat: satSum / pixelCount,
    sobelMag, sobelAngle, screenshotConfidence,
    highlightClip, shadowClip,
    dominantColors, noiseLevel, colorfulness, wbDeviation,
  };
}

// ═══════════════════════════════════════════════════════════════
// SECTION 4: Lighting — measurement-first, full 0-10 range
// ═══════════════════════════════════════════════════════════════

function scoreLighting(pa: PixelAnalysis): { score: number; note: string; warmthRatio: number; contrastStdDev: number; kelvin: number; isGoldenHour: boolean } {
  const { histogram, pixelCount, avgR, avgB } = pa;

  let shadowClip = 0, highlightClip = 0;
  for (let i = 0; i < 5; i++) shadowClip += histogram[i];
  for (let i = 251; i < 256; i++) highlightClip += histogram[i];
  const shadowPct = shadowClip / pixelCount;
  const highlightPct = highlightClip / pixelCount;
  const exposureQuality = 1.0 - Math.min(1, shadowPct * 5 + highlightPct * 8);

  let p1 = 0, p99 = 255, cum = 0;
  for (let i = 0; i < 256; i++) {
    cum += histogram[i];
    if (cum >= pixelCount * 0.01 && p1 === 0) p1 = i;
    if (cum >= pixelCount * 0.99) { p99 = i; break; }
  }
  const rangeQuality = Math.min(1, (p99 - p1) / 200);

  let lumMean = 0;
  for (let i = 0; i < 256; i++) lumMean += i * histogram[i];
  lumMean /= pixelCount;
  let lumVar = 0;
  for (let i = 0; i < 256; i++) lumVar += histogram[i] * (i - lumMean) ** 2;
  const stdDev = Math.sqrt(lumVar / pixelCount);
  const contrastQuality = stdDev < 20 ? 0.2 : stdDev < 40 ? 0.5 + (stdDev - 20) / 40 :
                          stdDev < 70 ? 1.0 : stdDev < 85 ? 0.6 : 0.3;

  // Color temperature estimation (McCamy's approximation from R/B ratio)
  const rbRatio = avgR / Math.max(avgB, 0.01);
  const kelvin = Math.max(2000, Math.min(15000, Math.round(6500 / rbRatio)));

  // Golden hour detection: warm color temp + soft shadows + no harsh highlights
  const isGoldenHour = kelvin >= 3000 && kelvin <= 4500
    && shadowPct < 0.15 && highlightPct < 0.10;
  const goldenHourBonus = isGoldenHour ? 1.0 : 0;

  // White balance accuracy (from gray-world deviation)
  const wbQuality = pa.wbDeviation < 0.08 ? 1.0
    : pa.wbDeviation < 0.15 ? 0.8
    : pa.wbDeviation < 0.25 ? 0.6
    : 0.3;

  const raw = (exposureQuality * 0.40 + rangeQuality * 0.18 + contrastQuality * 0.18 + wbQuality * 0.12 + goldenHourBonus * 0.12) * 10;

  let note = '';
  if (highlightPct > 0.05) note = 'Significant highlight clipping';
  else if (shadowPct > 0.10) note = 'Underexposed — dark shadows';
  else if (stdDev < 25) note = 'Low contrast — flat or washed out';
  else if (isGoldenHour) note = 'Beautiful golden hour lighting';
  else if (raw >= 7) note = 'Well-lit with good tonal range';
  else if (raw >= 5) note = 'Acceptable lighting';
  else note = 'Poor lighting conditions';

  return { score: Math.max(0, Math.min(10, raw)), note, warmthRatio: rbRatio, contrastStdDev: stdDev, kelvin, isGoldenHour };
}

// ═══════════════════════════════════════════════════════════════
// SECTION 5: Composition — strategy detection + penalties
// ═══════════════════════════════════════════════════════════════

// ── Selfie-aware composition: headroom, head placement, bg simplicity, body framing ──
function scoreSelfieComposition(
  pa: PixelAnalysis, width: number, height: number, faceLandmarks: any
): { score: number; note: string; thirdsScore: number; symmetryScore: number; bgComplexity: number; leadingLinesScore: number; negativeSpaceRatio: number } {
  const lms = faceLandmarks;
  if (!lms || lms.length < 10) return { score: 3.0, note: 'Selfie framing — limited landmark data', thirdsScore: 0.3, symmetryScore: 0.5, bgComplexity: 0.5, leadingLinesScore: 0, negativeSpaceRatio: 0.5 };

  // Head placement: eyes should be in upper third (0.28-0.38)
  const eyeY = lms[468]?.y ?? lms[4]?.y ?? 0.5;
  let headPlacement: number;
  if (eyeY >= 0.28 && eyeY <= 0.38) headPlacement = 10.0;
  else if (eyeY >= 0.20 && eyeY < 0.28) headPlacement = 7.0;
  else if (eyeY > 0.38 && eyeY <= 0.50) headPlacement = 6.0;
  else if (eyeY > 0.50) headPlacement = 3.0; // Eyes too low = too much forehead/ceiling
  else headPlacement = 4.0; // Eyes very high = forehead cropped

  // Headroom: space above top of head
  const ys = lms.map((l: any) => l.y);
  const topOfHead = Math.min(...ys);
  const headroomRatio = topOfHead; // 0 = cropped at top, 0.15 = good headroom
  let headroom: number;
  if (headroomRatio >= 0.08 && headroomRatio <= 0.20) headroom = 10.0;
  else if (headroomRatio > 0.20 && headroomRatio <= 0.30) headroom = 7.0;
  else if (headroomRatio > 0.30) headroom = 4.0; // Too much space above
  else if (headroomRatio > 0.03) headroom = 5.0;
  else headroom = 2.0; // Cropped at hairline

  // Background simplicity: edge density OUTSIDE face bounding box
  const xs = lms.map((l: any) => l.x);
  const faceMinX = Math.floor(Math.min(...xs) * width);
  const faceMaxX = Math.ceil(Math.max(...xs) * width);
  const faceMinY = Math.floor(Math.min(...ys) * height);
  const faceMaxY = Math.ceil(Math.max(...ys) * height);
  let bgEdgeCount = 0, bgPixelCount = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (x >= faceMinX && x <= faceMaxX && y >= faceMinY && y <= faceMaxY) continue;
      bgPixelCount++;
      if (pa.sobelMag[y * width + x] > 30) bgEdgeCount++;
    }
  }
  const bgEdgeRatio = bgPixelCount > 0 ? bgEdgeCount / bgPixelCount : 0.5;
  let bgSimplicity: number;
  if (bgEdgeRatio < 0.05) bgSimplicity = 10.0;
  else if (bgEdgeRatio < 0.10) bgSimplicity = 8.0;
  else if (bgEdgeRatio < 0.18) bgSimplicity = 6.0;
  else if (bgEdgeRatio < 0.25) bgSimplicity = 4.0;
  else bgSimplicity = 2.0;

  // Body framing: check if face bbox covers >70% of frame height (floating head)
  const faceHeightRatio = (Math.max(...ys) - Math.min(...ys));
  let bodyFraming: number;
  if (faceHeightRatio > 0.75) bodyFraming = 3.0; // Extreme close-up, no body context
  else if (faceHeightRatio > 0.60) bodyFraming = 5.0; // Tight framing
  else if (faceHeightRatio > 0.35) bodyFraming = 8.0; // Good: visible shoulders
  else bodyFraming = 7.0; // Far enough to see torso

  const score = headPlacement * 0.30 + headroom * 0.20 + bgSimplicity * 0.30 + bodyFraming * 0.20;

  let note: string;
  if (score >= 7) note = 'Well-framed selfie — good distance, clean background';
  else if (score >= 5) note = 'Good selfie distance — background could be simpler';
  else if (score >= 3) note = 'Selfie could use more headroom and cleaner background';
  else note = 'Very tight selfie framing — hold phone further back';

  // Real facial symmetry from landmarks — nose-to-cheek distance comparison
  let symmetryScore = 0.5;
  const noseTipLm = lms[4];
  const leftCheekLm = lms[234];
  const rightCheekLm = lms[454];
  if (noseTipLm && leftCheekLm && rightCheekLm) {
    const leftDist = Math.hypot(leftCheekLm.x - noseTipLm.x, leftCheekLm.y - noseTipLm.y);
    const rightDist = Math.hypot(rightCheekLm.x - noseTipLm.x, rightCheekLm.y - noseTipLm.y);
    symmetryScore = Math.min(leftDist, rightDist) / Math.max(leftDist, rightDist);
  }

  return {
    score: Math.max(0, Math.min(10, score)), note,
    thirdsScore: headPlacement / 10,
    symmetryScore,
    bgComplexity: bgEdgeRatio,
    leadingLinesScore: 0,
    negativeSpaceRatio: 1 - bgEdgeRatio,
  };
}

// ── Subject isolation heuristic for multi-person scenes ──
// When multiple faces are detected, measures whether one face dominates (clear central subject)
// vs. all faces being equally prominent (group shot). Returns a composition bonus.
// Background intentionality: low edge density outside face regions = intentional bokeh/simplicity
function computeBackgroundIntentionality(
  sobelMag: Float32Array, width: number, height: number,
  faceLandmarks: any[],
): number {
  // Build face bounding boxes with 20% padding
  const faceBboxes: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
  for (const face of faceLandmarks) {
    if (!face || face.length < 10) continue;
    const xs = face.map((l: any) => l.x * width);
    const ys = face.map((l: any) => l.y * height);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const padW = (maxX - minX) * 0.2, padH = (maxY - minY) * 0.2;
    faceBboxes.push({
      x1: Math.max(0, minX - padW), y1: Math.max(0, minY - padH),
      x2: Math.min(width, maxX + padW), y2: Math.min(height, maxY + padH),
    });
  }

  // Compute edge density in non-face regions (stride-2 for ~4× perf)
  let bgPixels = 0, bgEdgePixels = 0;
  const stride = 2;
  for (let y = 0; y < height; y += stride) {
    for (let x = 0; x < width; x += stride) {
      let inFace = false;
      for (const bb of faceBboxes) {
        if (x >= bb.x1 && x <= bb.x2 && y >= bb.y1 && y <= bb.y2) { inFace = true; break; }
      }
      if (inFace) continue;
      bgPixels++;
      if (sobelMag[y * width + x] > 30) bgEdgePixels++;
    }
  }

  if (bgPixels < 100) return 0;
  const bgEdgeDensity = bgEdgePixels / bgPixels;

  // Low background edge density = intentional clean/bokeh background
  if (bgEdgeDensity < 0.05) return 0.5;
  if (bgEdgeDensity < 0.08) return 0.3;
  if (bgEdgeDensity < 0.12) return 0.15;
  return 0;
}

function subjectIsolationBonus(faces: any[], faceCount: number): number {
  if (faceCount < 2 || !faces || faces.length < 2) return 0;
  const areas = faces.map((f: any) => {
    if (!f || f.length < 10) return 0;
    const xs = f.map((l: any) => l.x);
    const ys = f.map((l: any) => l.y);
    return (Math.max(...xs) - Math.min(...xs)) * (Math.max(...ys) - Math.min(...ys));
  }).filter((a: number) => a > 0);
  if (areas.length < 2) return 0;
  const maxArea = Math.max(...areas);
  const avgArea = areas.reduce((s: number, a: number) => s + a, 0) / areas.length;
  const isolationRatio = maxArea / avgArea;
  // Strong isolation: one face 2.5x+ bigger than average → clear central subject
  if (isolationRatio > 2.5) return 1.5;
  if (isolationRatio > 1.8) return 1.0;
  if (isolationRatio > 1.3) return 0.5;
  return 0;
}

function scoreComposition(
  pa: PixelAnalysis, width: number, height: number,
  selfieContext?: { isSelfie: boolean; faceLandmarks: any },
  modeId?: string,
): { score: number; note: string; thirdsScore: number; symmetryScore: number; bgComplexity: number; leadingLinesScore: number; negativeSpaceRatio: number } {
  if (selfieContext?.isSelfie && selfieContext.faceLandmarks) {
    return scoreSelfieComposition(pa, width, height, selfieContext.faceLandmarks[0]);
  }
  const { sobelMag, sobelAngle } = pa;
  const salThreshold = 40;

  let thirdsHits = 0, totalSalient = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (sobelMag[idx] > salThreshold) {
        totalSalient++;
        const xr = x / width, yr = y / height;
        if ((Math.abs(xr - 0.333) < 0.06 || Math.abs(xr - 0.667) < 0.06) ||
            (Math.abs(yr - 0.333) < 0.06 || Math.abs(yr - 0.667) < 0.06)) {
          thirdsHits++;
        }
      }
    }
  }
  const thirdsScore = totalSalient > 0 ? Math.min(1, (thirdsHits / totalSalient) / 0.30) : 0.3;

  let leftWeight = 0, rightWeight = 0;
  const halfW = Math.floor(width / 2);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < halfW; x++) {
      leftWeight += sobelMag[y * width + x];
      rightWeight += sobelMag[y * width + (width - 1 - x)];
    }
  }
  const total = leftWeight + rightWeight;
  const symmetryScore = total > 0 ? 1.0 - Math.abs(leftWeight - rightWeight) / total : 0.5;

  // Weighted average instead of max — prevents accidental high scores
  const strategyScore = thirdsScore * 0.6 + symmetryScore * 0.4;
  const strategyName = thirdsScore >= symmetryScore ? 'Rule of thirds' : 'Centered symmetry';

  const hAngles: number[] = [];
  for (let i = 0; i < sobelMag.length; i++) {
    if (sobelMag[i] > 40) {
      const deg = sobelAngle[i] * (180 / Math.PI);
      if (Math.abs(deg) > 70 && Math.abs(deg) < 110) hAngles.push(deg - 90);
    }
  }
  hAngles.sort((a, b) => a - b);
  const tilt = hAngles.length > 0 ? Math.abs(hAngles[Math.floor(hAngles.length / 2)]) : 0;
  const tiltPenalty = Math.min(2.5, tilt * 0.4);

  let edgeCount = 0;
  for (let i = 0; i < sobelMag.length; i++) { if (sobelMag[i] > 30) edgeCount++; }
  const clutterRatio = edgeCount / (width * height);
  // Mode-aware clutter thresholds: event/sports scenes are expected to be busy
  const isLenientClutter = modeId === 'event' || modeId === 'sports';
  const clutterPenalty = isLenientClutter
    ? (clutterRatio > 0.40 ? 2.0 : clutterRatio > 0.30 ? 1.0 : 0)
    : (clutterRatio > 0.30 ? 3.0 : clutterRatio > 0.22 ? 1.5 : clutterRatio > 0.15 ? 0.5 : 0);

  // Text-density heuristic: high edge density + scattered thirds alignment = likely meme/text overlay
  const textPenalty = (clutterRatio > 0.20 && totalSalient > 0 && thirdsHits / totalSalient > 0.15) ? 2.0 : 0;

  // Leading lines and negative space analysis
  const leadingLines = detectLeadingLines(sobelMag, sobelAngle, width, height);
  const negativeSpace = detectNegativeSpace(sobelMag, width, height);

  const raw = Math.max(0, strategyScore * 7 + leadingLines.score * 1.5 + negativeSpace.score * 1.5 - tiltPenalty - clutterPenalty - textPenalty);
  const note = `${strategyName} composition${leadingLines.score > 0.5 ? ', strong leading lines' : ''}${tilt > 3 ? ', slight tilt detected' : ''}${textPenalty > 0 ? ', text overlay detected' : ''}`;

  // Composition smoothing: round to nearest 0.5 to reduce noise between similar images
  const smoothed = Math.round(Math.max(0, Math.min(10, raw)) * 2) / 2;

  return {
    score: smoothed, note,
    thirdsScore, symmetryScore,
    bgComplexity: clutterRatio,
    leadingLinesScore: leadingLines.score,
    negativeSpaceRatio: negativeSpace.ratio,
  };
}

// ═══════════════════════════════════════════════════════════════
// SECTION 6: Scene Interest — vibrancy + complexity + NIMA blend
// ═══════════════════════════════════════════════════════════════

function scoreSceneInterest(pa: PixelAnalysis, nimaScore: number): { score: number; note: string } {
  const { sobelMag } = pa;

  // Use Hasler-Süsstrunk colorfulness instead of crude avgSat*2
  const vibrancy = Math.min(0.85, pa.colorfulness);

  let edgeCount = 0;
  for (let i = 0; i < sobelMag.length; i++) { if (sobelMag[i] > 25) edgeCount++; }
  const edgeRatio = edgeCount / sobelMag.length;
  const complexity = edgeRatio < 0.03 ? 0.2 : edgeRatio < 0.08 ? 0.6 :
                     edgeRatio < 0.18 ? 1.0 : edgeRatio < 0.30 ? 0.7 : 0.4;

  // Reduce vibrancy weight — prevents saturated memes from scoring high
  const canvasRaw = (vibrancy * 0.3 + complexity * 0.7) * 10;

  let raw: number;
  if (nimaScore > 0) {
    // When NIMA signals low quality, trust it more heavily
    const nimaWeight = nimaScore < 5.0 ? 0.7 : 0.6;
    raw = canvasRaw * (1 - nimaWeight) + nimaScore * nimaWeight;
  } else {
    raw = canvasRaw;
  }

  const note = raw >= 7 ? 'Visually engaging scene' : raw >= 5 ? 'Moderate visual interest' :
               raw >= 3 ? 'Limited visual interest' : 'Very low visual interest';

  return { score: Math.max(0, Math.min(10, raw)), note };
}

// ═══════════════════════════════════════════════════════════════
// SECTION 7: Portrait Composition — multi-face, Duchenne, relevance
// ═══════════════════════════════════════════════════════════════

// ── Head pose analysis from 3D face landmarks ──
// MediaPipe provides 478 landmarks with x,y,z coordinates.
// We use key landmarks to estimate pitch (up/down), yaw (left/right), roll (tilt).
interface HeadPoseResult {
  pitch: number;  // degrees: positive = looking up, negative = looking down
  yaw: number;    // degrees: positive = turned right, negative = turned left
  roll: number;   // degrees: head tilt
  bonus: number;  // score adjustment from pose
  note: string;
}

function analyzeHeadPose(landmarks: any[]): HeadPoseResult {
  if (!landmarks || landmarks.length < 400) {
    return { pitch: 0, yaw: 0, roll: 0, bonus: 0, note: '' };
  }

  // Key landmarks: nose tip (4), chin (152), forehead (10), left ear (234), right ear (454)
  const noseTip = landmarks[4];
  const chin = landmarks[152];
  const forehead = landmarks[10];
  const leftEar = landmarks[234];
  const rightEar = landmarks[454];
  const leftEye = landmarks[33];
  const rightEye = landmarks[263];

  if (!noseTip || !chin || !forehead || !leftEar || !rightEar) {
    return { pitch: 0, yaw: 0, roll: 0, bonus: 0, note: '' };
  }

  // Yaw: asymmetry of nose position relative to ears
  const earMidX = (leftEar.x + rightEar.x) / 2;
  const yawRaw = (noseTip.x - earMidX) * 100; // rough degrees approximation
  const yaw = Math.max(-45, Math.min(45, yawRaw * 2));

  // Roll: eye line tilt
  const eyeDeltaY = rightEye.y - leftEye.y;
  const eyeDeltaX = rightEye.x - leftEye.x;
  const roll = Math.atan2(eyeDeltaY, eyeDeltaX) * (180 / Math.PI);

  // Pitch: nose-to-chin vs nose-to-forehead ratio
  const noseToForehead = Math.abs(noseTip.y - forehead.y);
  const noseToChin = Math.abs(chin.y - noseTip.y);
  const pitchRatio = noseToForehead > 0 ? noseToChin / noseToForehead : 1;
  const pitch = (pitchRatio - 1.0) * 30; // positive = looking down

  let bonus = 0;
  const notes: string[] = [];

  // Yaw: slight angle (8-18°) is flattering for selfies/portraits
  const absYaw = Math.abs(yaw);
  if (absYaw >= 8 && absYaw <= 18) bonus += 0.5;
  else if (absYaw >= 5 && absYaw < 8) bonus += 0.2;
  else if (absYaw > 25) { bonus -= 1.0; notes.push('Head turned too far to side'); }
  else if (absYaw > 18) { bonus -= 0.3; }

  // Roll: natural head tilts are common in candid/portrait photos
  const absRoll = Math.abs(roll);
  if (absRoll > 25) { bonus -= 1.0; notes.push('Head tilted significantly'); }
  else if (absRoll > 15) { bonus -= 0.3; }
  // 0-15° = no penalty (natural range for candid photos)

  // Pitch: looking slightly up is flattering, looking down creates double chin
  if (pitch > 10) { bonus -= 0.3; notes.push('Looking down — try lifting chin'); }
  else if (pitch < -15) { bonus -= 0.5; notes.push('Head tilted back too far'); }
  else if (pitch >= -5 && pitch <= 5) bonus += 0.3; // Near level is good

  return { pitch, yaw, roll, bonus, note: notes.join(', ') };
}

// ── Jawline definition analysis from landmarks ──
function analyzeJawline(landmarks: any[]): { bonus: number; note: string } {
  if (!landmarks || landmarks.length < 400) return { bonus: 0, note: '' };

  // Jawline landmarks: 0-16 in MediaPipe are along the jawline
  // We measure jawline definition by checking the sharpness of the jaw angle
  const jawLeft = landmarks[234];   // Left ear area
  const jawRight = landmarks[454];  // Right ear area
  const chin = landmarks[152];      // Chin point
  const jawAngleLeft = landmarks[172];  // Left jaw angle
  const jawAngleRight = landmarks[397]; // Right jaw angle

  if (!jawLeft || !jawRight || !chin || !jawAngleLeft || !jawAngleRight) {
    return { bonus: 0, note: '' };
  }

  // Jaw definition: sharper angle at jawline = more defined
  // Measure the angle formed at jaw corners
  const leftAngle = Math.atan2(chin.y - jawAngleLeft.y, chin.x - jawAngleLeft.x) -
                     Math.atan2(jawLeft.y - jawAngleLeft.y, jawLeft.x - jawAngleLeft.x);
  const rightAngle = Math.atan2(chin.y - jawAngleRight.y, chin.x - jawAngleRight.x) -
                      Math.atan2(jawRight.y - jawAngleRight.y, jawRight.x - jawAngleRight.x);

  const avgAngleDeg = (Math.abs(leftAngle) + Math.abs(rightAngle)) / 2 * (180 / Math.PI);

  // Sharp jaw (80-120°) = defined, good for portraits
  // Wide jaw angle (>140°) = softer, less defined
  let bonus = 0;
  if (avgAngleDeg >= 80 && avgAngleDeg <= 120) bonus = 0.5;
  else if (avgAngleDeg > 140) bonus = -0.3;

  // Jaw symmetry
  const jawSymmetry = 1.0 - Math.abs(Math.abs(leftAngle) - Math.abs(rightAngle)) / Math.PI;
  if (jawSymmetry > 0.8) bonus += 0.3;

  return { bonus, note: '' };
}

// ── Eye openness granularity from landmarks ──
function analyzeEyeOpenness(landmarks: any[]): { bonus: number; note: string } {
  if (!landmarks || landmarks.length < 400) return { bonus: 0, note: '' };

  // Upper/lower eyelid landmarks
  // Left eye: upper 159, lower 145
  // Right eye: upper 386, lower 374
  const leftUpper = landmarks[159];
  const leftLower = landmarks[145];
  const rightUpper = landmarks[386];
  const rightLower = landmarks[374];

  if (!leftUpper || !leftLower || !rightUpper || !rightLower) return { bonus: 0, note: '' };

  const leftOpenness = Math.abs(leftUpper.y - leftLower.y);
  const rightOpenness = Math.abs(rightUpper.y - rightLower.y);
  const avgOpenness = (leftOpenness + rightOpenness) / 2;

  // Eye openness symmetry
  const eyeSymmetry = 1.0 - Math.abs(leftOpenness - rightOpenness) / (avgOpenness + 0.001);

  let bonus = 0;
  // Wide open, alert eyes
  if (avgOpenness > 0.025) bonus += 0.3;
  // Narrow/squinting (NOT from smiling)
  else if (avgOpenness < 0.012) bonus -= 0.5;

  // Reward symmetrical eye openness
  if (eyeSymmetry > 0.85) bonus += 0.2;
  else if (eyeSymmetry < 0.45) { bonus -= 0.3; }

  return { bonus, note: eyeSymmetry < 0.45 ? 'Uneven eye openness' : '' };
}

// ── Skin uniformity in face region from pixel data ──
function analyzeSkinUniformity(
  imageData: Uint8ClampedArray, width: number, height: number,
  landmarks: any[]
): { bonus: number; note: string } {
  if (!landmarks || landmarks.length < 400) return { bonus: 0, note: '' };

  // Sample skin pixels from cheek regions (stable, usually visible)
  const leftCheek = landmarks[50];
  const rightCheek = landmarks[280];
  if (!leftCheek || !rightCheek) return { bonus: 0, note: '' };

  // Sample a small region around each cheek
  const sampleRadius = Math.max(3, Math.round(width * 0.02));
  const samples: number[] = [];

  for (const cheek of [leftCheek, rightCheek]) {
    const cx = Math.round(cheek.x * width);
    const cy = Math.round(cheek.y * height);
    for (let dy = -sampleRadius; dy <= sampleRadius; dy++) {
      for (let dx = -sampleRadius; dx <= sampleRadius; dx++) {
        const px = cx + dx, py = cy + dy;
        if (px < 0 || px >= width || py < 0 || py >= height) continue;
        const idx = (py * width + px) * 4;
        // Luminance
        const lum = imageData[idx] * 0.299 + imageData[idx + 1] * 0.587 + imageData[idx + 2] * 0.114;
        samples.push(lum);
      }
    }
  }

  if (samples.length < 10) return { bonus: 0, note: '' };

  const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
  const variance = samples.reduce((a, b) => a + (b - mean) ** 2, 0) / samples.length;
  const stdDev = Math.sqrt(variance);

  // Low variance = even skin tone (good lighting on face, no harsh shadows)
  // High variance = uneven lighting, skin issues, or harsh shadows on face
  let bonus = 0;
  if (stdDev < 10) bonus = 0.3;       // Very even skin tone
  else if (stdDev < 18) bonus = 0.1;   // Acceptable
  else if (stdDev > 30) bonus = -0.5;  // Harsh shadows or very uneven
  else if (stdDev > 25) bonus = -0.2;

  return { bonus, note: stdDev > 30 ? 'Uneven lighting on face' : '' };
}

// ── Face lighting quality from pixel analysis ──
function analyzeFaceLighting(
  imageData: Uint8ClampedArray, width: number, height: number,
  landmarks: any[]
): { bonus: number; note: string } {
  if (!landmarks || landmarks.length < 400) return { bonus: 0, note: '' };

  // Sample left vs right face to detect side lighting
  const noseTip = landmarks[4];
  const leftCheek = landmarks[50];
  const rightCheek = landmarks[280];
  if (!noseTip || !leftCheek || !rightCheek) return { bonus: 0, note: '' };

  const sampleRadius = Math.max(2, Math.round(width * 0.015));

  function regionLuminance(cx: number, cy: number): number {
    let sum = 0, count = 0;
    for (let dy = -sampleRadius; dy <= sampleRadius; dy++) {
      for (let dx = -sampleRadius; dx <= sampleRadius; dx++) {
        const px = Math.round(cx) + dx, py = Math.round(cy) + dy;
        if (px < 0 || px >= width || py < 0 || py >= height) continue;
        const idx = (py * width + px) * 4;
        sum += imageData[idx] * 0.299 + imageData[idx + 1] * 0.587 + imageData[idx + 2] * 0.114;
        count++;
      }
    }
    return count > 0 ? sum / count : 128;
  }

  const leftLum = regionLuminance(leftCheek.x * width, leftCheek.y * height);
  const rightLum = regionLuminance(rightCheek.x * width, rightCheek.y * height);
  const noseLum = regionLuminance(noseTip.x * width, noseTip.y * height);

  // Lighting balance: large difference = harsh side lighting
  const sideRatio = Math.min(leftLum, rightLum) / (Math.max(leftLum, rightLum) + 0.001);
  // Average face brightness
  const avgFaceLum = (leftLum + rightLum + noseLum) / 3;

  let bonus = 0;
  const notes: string[] = [];

  // Even lighting (both sides similar) is generally best
  if (sideRatio > 0.85) bonus += 0.3;
  else if (sideRatio < 0.6) { bonus -= 0.8; notes.push('Harsh side lighting on face'); }
  else if (sideRatio < 0.7) { bonus -= 0.3; }

  // Face brightness: not too dark, not blown out
  if (avgFaceLum > 60 && avgFaceLum < 200) bonus += 0.2;
  else if (avgFaceLum < 40) { bonus -= 0.5; notes.push('Face underlit'); }
  else if (avgFaceLum > 230) { bonus -= 0.3; notes.push('Face overexposed'); }

  return { bonus, note: notes.join(', ') };
}

interface FaceResult {
  score: number;
  faceCount: number;
  faceRelevance: number;
  note: string;
  isSelfie: boolean;
  faceLandmarks: any[] | null;
  largestFaceRatio: number;
  expressionDetractors: { type: string; severity: number }[];
  expressionIssues: string[];  // Expression issues for chat context
  // PRISM aesthetic signals (extracted from expression analysis)
  smileIntensity: number;     // 0-1 average mouth smile blendshape
  eyeContact: number;         // 0-1 gaze consistency
  naturalness: number;        // 0-1 expression naturalness
}

// ── Selfie context detection ──
function detectSelfieContext(faceCount: number, largestFaceRatio: number, faces: any[]): boolean {
  if (faceCount !== 1) return false;
  if (largestFaceRatio < 0.15) return false;
  if (faces[0]?.length > 10) {
    const noseTip = faces[0][4]; // MediaPipe nose tip landmark
    if (noseTip) {
      const centerDev = Math.sqrt((noseTip.x - 0.5) ** 2 + (noseTip.y - 0.5) ** 2);
      if (centerDev > 0.35) return false;
    }
  }
  return true;
}

// ── Face-to-frame ratio bell curve (replaces old step function) ──
function faceToFrameMultiplier(ratio: number, isSelfie: boolean): number {
  if (isSelfie) {
    if (ratio >= 0.20 && ratio <= 0.45) return 1.0;
    if (ratio > 0.45 && ratio <= 0.60) return 1.0 - (ratio - 0.45) / 0.15 * 0.3;
    if (ratio > 0.60 && ratio <= 0.80) return 0.7 - (ratio - 0.60) / 0.20 * 0.3;
    if (ratio > 0.80) return 0.25;
    if (ratio >= 0.10 && ratio < 0.20) return 0.7 + (ratio - 0.10) / 0.10 * 0.3;
    return ratio < 0.05 ? 0.3 : 0.5;
  }
  if (ratio >= 0.15 && ratio <= 0.35) return 1.0;
  if (ratio > 0.35 && ratio <= 0.50) return 1.0 - (ratio - 0.35) / 0.15 * 0.3;
  if (ratio > 0.50 && ratio <= 0.70) return 0.7 - (ratio - 0.50) / 0.20 * 0.3;
  if (ratio > 0.70) return 0.2;
  if (ratio >= 0.08 && ratio < 0.15) return 0.7 + (ratio - 0.08) / 0.07 * 0.3;
  return ratio < 0.03 ? 0.2 : 0.4;
}

// Mode-awareness is handled at TWO levels, NOT here:
// 1. Portrait mode weight (0.40 Dating, 0.15 Travel) → bad portrait hurts Dating more
// 2. detractorPenaltyStrength (1.5 Dating, 2.0 RealEstate) → photo-ruining flaws hit harder
// Expression penalties are UNIVERSAL — a duck face looks bad regardless of mode.
// The mode system naturally amplifies portrait issues through the weight profile.

// ── Elite expression analysis using MediaPipe blendshapes ──
interface ExpressionResult {
  score: number;
  issues: string[];
  detractors: { type: string; severity: number }[]; // Formal detractor penalties
  topSuggestion: string;
  goofySeverity: number;      // 0-1 cumulative goofy expression severity
  // PRISM aesthetic signals (raw blendshape-derived values)
  smileIntensity: number;     // 0-1 average mouth smile
  eyeContact: number;         // 0-1 gaze consistency (1 = looking at camera)
  naturalness: number;        // 0-1 expression naturalness
}

function analyzeExpression(getBS: (name: string) => number): ExpressionResult {
  // Base 5.0 = neutral face. Bonuses push toward 10, penalties toward 0.
  // Calibrated so the full range is used:
  //   Perfect portrait: ~9.5  |  Good natural: ~7.5  |  Neutral: ~6.5
  //   Slightly off: ~4.5      |  Duck face: ~1.5-2.5  |  Eyes closed: ~2.0
  let score = 5.0;
  const issues: string[] = [];
  // Only truly photo-ruining issues become formal detractors (multiplicative on composite).
  // Expression quality issues stay in the portrait dimension score only — the mode weight
  // system (0.40 for Dating, 0.15 for Travel) naturally amplifies their impact per mode.
  const detractors: { type: string; severity: number }[] = [];

  // ── Duck face detection ──
  // Portrait-dimension penalty only — not a formal detractor because lighting/scene can
  // still be excellent. Mode weights handle the amplification (Dating 0.40 vs Travel 0.15).
  const mouthPucker = getBS('mouthPucker');
  const mouthFunnel = getBS('mouthFunnel');
  const cheekSquintAvg = (getBS('cheekSquintLeft') + getBS('cheekSquintRight')) / 2;
  if (mouthPucker > 0.6) {
    score -= 3.5;
    issues.push('Duck face detected');
  } else if (mouthPucker > 0.4 && cheekSquintAvg > 0.15) {
    score -= 2.5;
    issues.push('Duck face detected');
  } else if (mouthPucker > 0.35) {
    score -= 1.0;
    issues.push('Pursed lips');
  }

  // ── Facial symmetry scoring ──
  const asymmetryPairs = [
    Math.abs(getBS('mouthSmileLeft') - getBS('mouthSmileRight')),
    Math.abs(getBS('browDownLeft') - getBS('browDownRight')),
    Math.abs(getBS('cheekSquintLeft') - getBS('cheekSquintRight')),
    Math.abs(getBS('eyeSquintLeft') - getBS('eyeSquintRight')),
    Math.abs(getBS('browOuterUpLeft') - getBS('browOuterUpRight')),
    Math.abs(getBS('mouthLeft') - getBS('mouthRight')),
    Math.abs(getBS('mouthShrugUpper') - getBS('mouthShrugLower')),
    Math.abs(getBS('eyeWideLeft') - getBS('eyeWideRight')),
  ];
  const avgAsymmetry = asymmetryPairs.reduce((a, b) => a + b, 0) / asymmetryPairs.length;
  // Smirks and natural asymmetric expressions are normal — only penalize extreme cases
  if (avgAsymmetry > 0.65) {
    score -= 0.8;
    issues.push('Very uneven expression');
  } else if (avgAsymmetry > 0.45) {
    score -= 0.3;
    issues.push('Slightly uneven expression');
  } else if (avgAsymmetry < 0.15) {
    score += 0.5;
  }

  // ── Double chin detection ──
  const jawForward = getBS('jawForward');
  const jawOpen = getBS('jawOpen');
  const jawLeftRight = Math.abs(getBS('jawLeft') - getBS('jawRight'));
  if (jawForward > 0.3 && jawOpen < 0.15) {
    score -= 1.0;
    issues.push('Unflattering chin angle');
  }
  if (jawLeftRight > 0.3) {
    score -= 0.3;
  }

  // ── Mouth tension / lip press ──
  const mouthShrugUpper = getBS('mouthShrugUpper');
  const mouthShrugLower = getBS('mouthShrugLower');
  if (mouthShrugUpper > 0.5 && mouthShrugLower > 0.5) {
    score -= 0.5;
    issues.push('Tense mouth');
  }

  // ── Goofy / exaggerated face detection ──
  // CUMULATIVE: multiple goofy features compound (not else-if).
  // goofySeverity drives bonus gating + composite-level detractor.
  const eyeWideL = getBS('eyeWideLeft'), eyeWideR = getBS('eyeWideRight');
  const browInnerUp = getBS('browInnerUp');
  let goofySeverity = 0;

  if (eyeWideL > 0.5 && eyeWideR > 0.5 && jawOpen > 0.4) {
    goofySeverity += 0.5; score -= 2.5;
    issues.push('Exaggerated expression');
  }
  if (mouthFunnel > 0.5 && browInnerUp > 0.4) {
    goofySeverity += 0.4; score -= 2.0;
    issues.push('Exaggerated mouth');
  }
  if (Math.abs(eyeWideL - eyeWideR) > 0.6) {
    goofySeverity += 0.15; score -= 0.8;
    issues.push('Asymmetric expression');
  }
  if (getBS('tongueOut') > 0.3) {
    const tongueVal = getBS('tongueOut');
    const penalty = tongueVal > 0.6 ? 3.5 : tongueVal > 0.45 ? 3.0 : 2.5;
    goofySeverity += 0.5; score -= penalty;
    issues.push('Tongue out');
  }
  if (getBS('cheekPuff') > 0.5) {
    goofySeverity += 0.2; score -= 1.5;
    issues.push('Puffed cheeks');
  }

  // ── Expression naturalness tiers ──
  const mouthSmile = (getBS('mouthSmileLeft') + getBS('mouthSmileRight')) / 2;
  const eyeSquintAvg = (getBS('eyeSquintLeft') + getBS('eyeSquintRight')) / 2;
  const isDuckFace = issues.some(i => i.includes('Duck face'));
  const isExaggerated = goofySeverity > 0;

  if (!isDuckFace && !isExaggerated) {
    if (mouthSmile > 0.4 && cheekSquintAvg > 0.2 && eyeSquintAvg > 0.1) {
      score += 2.5; // Genuine Duchenne smile
    } else if (mouthSmile > 0.15 && mouthSmile <= 0.35) {
      score += 1.5; // Slight natural smile
    } else if (mouthSmile > 0.3 && cheekSquintAvg < 0.1) {
      score += 0.3; // Forced smile — barely better than neutral
      if (mouthSmile > 0.5) issues.push('Smile looks forced');
    } else if (mouthSmile < 0.15 && jawOpen < 0.1 && mouthPucker < 0.15) {
      score += 1.0; // Relaxed neutral
    }
  }

  // ── Closed eyes — BOTH a portrait penalty AND a formal detractor ──
  // A photo with closed eyes is essentially ruined regardless of other qualities.
  // The detractor applies multiplicatively to the composite, affecting ALL dimensions.
  const blinkL = getBS('eyeBlinkLeft'), blinkR = getBS('eyeBlinkRight');
  if (blinkL > 0.5 && blinkR > 0.5) {
    score -= 2.5;
    issues.push('Eyes closed');
    detractors.push({ type: 'closed_eyes', severity: 0.18 });
  } else if (blinkL > 0.5 || blinkR > 0.5) {
    score -= 1.5;
    issues.push('One eye closed');
    detractors.push({ type: 'closed_eyes', severity: 0.08 });
  } else {
    score += 0.5;
  }

  // ── Squint penalty (not from smiling) — graduated tiers ──
  if (!isDuckFace && cheekSquintAvg < 0.15) {
    if (eyeSquintAvg > 0.7) {
      score -= 1.5; goofySeverity += 0.3;
      issues.push('Eyes heavily squinted');
    } else if (eyeSquintAvg > 0.5) {
      score -= 0.8; goofySeverity += 0.2;
      issues.push('Eyes squinted');
    } else if (eyeSquintAvg > 0.35) {
      score -= 0.3;
      issues.push('Slightly squinting');
    }
  }

  // ── Formal bad_expression composite-level detractor ──
  // Multiplicative on the ENTIRE composite, not just portrait dimension.
  if (goofySeverity >= 0.7) {
    detractors.push({ type: 'bad_expression', severity: 0.15 });
  } else if (goofySeverity >= 0.5) {
    detractors.push({ type: 'bad_expression', severity: 0.10 });
  } else if (goofySeverity >= 0.3) {
    detractors.push({ type: 'bad_expression', severity: 0.05 });
  }

  // ── Gaze consistency ──
  const gazeIn = Math.abs(getBS('eyeLookInLeft') - getBS('eyeLookInRight'));
  if (gazeIn < 0.15) score += 0.3;

  // ── Gaze direction (looking away) ──
  const lookOutL = getBS('eyeLookOutLeft'), lookOutR = getBS('eyeLookOutRight');
  if (lookOutL > 0.4 || lookOutR > 0.4) {
    score -= 0.4;
  }
  const lookUpAvg = (getBS('eyeLookUpLeft') + getBS('eyeLookUpRight')) / 2;
  const lookDownAvg = (getBS('eyeLookDownLeft') + getBS('eyeLookDownRight')) / 2;
  if (lookUpAvg > 0.5 || lookDownAvg > 0.5) {
    score -= 0.3;
  }

  // ── Nose sneer (contempt micro-expression) ──
  const sneerAvg = (getBS('noseSneerLeft') + getBS('noseSneerRight')) / 2;
  if (sneerAvg > 0.4) {
    score -= 0.7;
    issues.push('Unflattering expression');
  }

  // Build suggestion from top issue
  const suggestionMap: Record<string, string> = {
    'Duck face detected': 'Natural expressions photograph significantly better',
    'Pursed lips': 'Try relaxing your lips for a more natural look',
    'Very uneven expression': 'Try relaxing both sides of your face evenly',
    'Uneven expression': 'Aim for a more balanced, symmetrical expression',
    'Unflattering chin angle': 'Try lifting chin slightly or angling camera higher',
    'Exaggerated expression': 'This will score low in any mode — try natural',
    'Extreme asymmetric expression': 'Even out your expression for a better score',
    'Tongue out': 'Natural expressions score much higher',
    'Puffed cheeks': 'Relax your cheeks for a more natural look',
    'Smile looks forced': 'Think of something genuinely funny for an authentic smile',
    'Eyes closed': 'Try again with eyes open',
    'One eye closed': 'Make sure both eyes are open and relaxed',
    'Eyes squinted': 'Try opening your eyes more naturally',
    'Eyes heavily squinted': 'Open your eyes naturally — avoid squinting',
    'Slightly squinting': 'Try to relax your eyes slightly',
    'Exaggerated mouth': 'Keep your mouth relaxed and natural',
    'Unflattering expression': 'Relax your face for a more appealing look',
  };
  const topSuggestion = issues.length > 0
    ? (suggestionMap[issues[0]] ?? 'Try a more natural, relaxed expression')
    : 'Great expression';

  // PRISM aesthetic signals
  const prismSmile = Math.min(1, mouthSmile);
  // Eye contact: high when gaze is consistent (gazeIn < 0.15) and not looking out/up/down
  const gazeConsistency = 1 - Math.min(1, gazeIn * 3);
  const lookAway = Math.max(lookOutL, lookOutR, lookUpAvg, lookDownAvg);
  const prismEyeContact = Math.max(0, gazeConsistency - lookAway * 0.5);
  // Naturalness: high for Duchenne smile / relaxed neutral, low for forced / exaggerated
  const prismNaturalness = isDuckFace ? 0.1 :
    goofySeverity > 0.5 ? 0.1 :
    goofySeverity > 0.3 ? 0.2 :
    isExaggerated ? 0.15 :
    (cheekSquintAvg > 0.2 && mouthSmile > 0.3) ? 0.9 :  // Duchenne
    (mouthSmile < 0.15 && jawOpen < 0.1) ? 0.8 :         // Relaxed neutral
    (mouthSmile > 0.3 && cheekSquintAvg < 0.1) ? 0.4 :   // Forced smile
    0.6; // Moderate

  return {
    score: Math.max(0, Math.min(10, score)), issues, detractors, topSuggestion,
    goofySeverity: Math.min(1, goofySeverity),
    smileIntensity: prismSmile, eyeContact: prismEyeContact, naturalness: prismNaturalness,
  };
}

// ── Blur detection from pixel analysis (Laplacian variance) ──
// Returns isDirectional to distinguish motion blur from uniform softness.
function detectBlur(pa: PixelAnalysis, width: number, height: number): { severity: number; note: string; isDirectional: boolean } {
  // Use Sobel magnitude as proxy for sharpness (true Laplacian not available from single pass)
  // Low edge density = blurry image
  let edgeSum = 0, edgeCount = 0;
  for (let i = 0; i < pa.sobelMag.length; i++) {
    edgeSum += pa.sobelMag[i];
    if (pa.sobelMag[i] > 20) edgeCount++;
  }
  const avgEdge = edgeSum / pa.sobelMag.length;
  const edgeRatio = edgeCount / pa.sobelMag.length;

  // Directional blur analysis: bin strong edge angles into 18 bins of 10°
  // If a 30° window (peak + neighbors) contains >60% of strong edges, it's directional/motion blur
  let isDirectional = false;
  if (edgeCount > 100 && pa.sobelAngle) {
    const bins = new Float64Array(18);
    let totalStrong = 0;
    for (let i = 0; i < pa.sobelMag.length; i++) {
      if (pa.sobelMag[i] > 20) {
        const deg = ((pa.sobelAngle[i] * 180 / Math.PI) % 180 + 180) % 180;
        const bin = Math.min(17, Math.floor(deg / 10));
        bins[bin]++;
        totalStrong++;
      }
    }
    if (totalStrong > 0) {
      let maxWindow = 0;
      for (let i = 0; i < 18; i++) {
        const window = bins[i] + bins[(i + 1) % 18] + bins[(i + 17) % 18]; // 30° window
        if (window > maxWindow) maxWindow = window;
      }
      isDirectional = maxWindow / totalStrong > 0.60;
    }
  }

  // Graduated blur severity — finer tiers for more accurate penalization
  if (avgEdge < 5 && edgeRatio < 0.03) {
    return { severity: 0.30, note: isDirectional ? 'Motion blur detected' : 'Significant blur detected', isDirectional };
  }
  if (avgEdge < 6 && edgeRatio < 0.04) {
    return { severity: 0.22, note: isDirectional ? 'Noticeable motion blur' : 'Noticeable blur', isDirectional };
  }
  if (avgEdge < 8 && edgeRatio < 0.05) {
    return { severity: 0.15, note: isDirectional ? 'Moderate motion blur' : 'Moderate blur — image lacks sharpness', isDirectional };
  }
  if (avgEdge < 10 && edgeRatio < 0.06) {
    return { severity: 0.10, note: isDirectional ? 'Slight motion blur' : 'Mild softness', isDirectional };
  }
  if (avgEdge < 12 && edgeRatio < 0.08) {
    return { severity: 0.05, note: isDirectional ? 'Trace motion blur' : 'Slight softness', isDirectional };
  }
  return { severity: 0, note: '', isDirectional: false };
}

// ── Severe exposure detection from histogram ──
function detectSevereExposure(pa: PixelAnalysis): { severity: number; note: string } {
  // Check for severe over/underexposure from highlight/shadow clipping
  const { highlightClip, shadowClip } = pa;

  if (highlightClip > 0.40) {
    return { severity: 0.25, note: 'Massively overexposed — image washed out' };
  }
  if (shadowClip > 0.45) {
    return { severity: 0.25, note: 'Massively underexposed — image nearly black' };
  }
  if (highlightClip > 0.25) {
    return { severity: 0.18, note: 'Severely overexposed — highlights blown out' };
  }
  if (shadowClip > 0.30) {
    return { severity: 0.18, note: 'Severely underexposed — deep shadow clipping' };
  }
  if (highlightClip > 0.15) {
    return { severity: 0.08, note: 'Overexposed highlights' };
  }
  if (shadowClip > 0.20) {
    return { severity: 0.08, note: 'Underexposed shadows' };
  }
  return { severity: 0, note: '' };
}

// ── Skin tone detection for face fallback ──
function detectSkinToneRegions(data: Uint8ClampedArray, w: number, h: number): number {
  let skinPixels = 0, total = 0;
  const startX = Math.floor(w * 0.25), endX = Math.floor(w * 0.75);
  const startY = Math.floor(h * 0.15), endY = Math.floor(h * 0.60);
  for (let y = startY; y < endY; y += 3) {
    for (let x = startX; x < endX; x += 3) {
      const i = (y * w + x) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const yc = 0.299 * r + 0.587 * g + 0.114 * b;
      const cb = 128 - 0.169 * r - 0.331 * g + 0.500 * b;
      const cr = 128 + 0.500 * r - 0.419 * g - 0.081 * b;
      if (yc > 80 && cb > 85 && cb < 135 && cr > 135 && cr < 180) skinPixels++;
      total++;
    }
  }
  return total > 0 ? skinPixels / total : 0;
}

// ── Noise detection from smooth-region luminance variance ──
function detectNoise(pa: PixelAnalysis): { severity: number; note: string } {
  if (pa.noiseLevel > 30) return { severity: 0.25, note: 'Severe image noise' };
  if (pa.noiseLevel > 20) return { severity: 0.15, note: 'High image noise' };
  if (pa.noiseLevel > 15) return { severity: 0.08, note: 'Moderate noise visible' };
  if (pa.noiseLevel > 12) return { severity: 0.03, note: 'Slight noise' };
  return { severity: 0, note: '' };
}

// ── Over-saturation detection — pixel-level saturation clipping ──
function detectOversaturation(data: Uint8ClampedArray, pixelCount: number): { severity: number; note: string } {
  let overSatCount = 0;
  for (let i = 0; i < data.length; i += 4) {
    const maxC = Math.max(data[i], data[i + 1], data[i + 2]);
    const minC = Math.min(data[i], data[i + 1], data[i + 2]);
    if (maxC > 0 && (maxC - minC) / maxC > 0.95) overSatCount++;
  }
  const ratio = overSatCount / pixelCount;
  if (ratio > 0.25) return { severity: 0.20, note: 'Extremely over-saturated' };
  if (ratio > 0.15) return { severity: 0.12, note: 'Significantly over-saturated' };
  if (ratio > 0.08) return { severity: 0.06, note: 'Over-saturated regions detected' };
  return { severity: 0, note: '' };
}

// ── Over-sharpening detection — halo artifacts at edges ──
function detectOversharpening(pa: PixelAnalysis, width: number, height: number): { severity: number; note: string } {
  let strongEdges = 0, haloEdges = 0;
  for (let y = 2; y < height - 2; y++) {
    for (let x = 2; x < width - 2; x++) {
      const idx = y * width + x;
      if (pa.sobelMag[idx] > 60) {
        strongEdges++;
        // Check perpendicular neighbors for secondary peaks (halos)
        const angle = pa.sobelAngle[idx];
        const perpX = Math.round(Math.sin(angle));
        const perpY = Math.round(-Math.cos(angle));
        const neighbor1 = (y + perpY * 2) * width + (x + perpX * 2);
        const neighbor2 = (y - perpY * 2) * width + (x - perpX * 2);
        if (neighbor1 >= 0 && neighbor1 < pa.sobelMag.length &&
            neighbor2 >= 0 && neighbor2 < pa.sobelMag.length) {
          // Halo: secondary edge peak 2px away from primary edge
          if (pa.sobelMag[neighbor1] > 30 || pa.sobelMag[neighbor2] > 30) {
            haloEdges++;
          }
        }
      }
    }
  }
  const haloRatio = strongEdges > 50 ? haloEdges / strongEdges : 0;
  if (haloRatio > 0.25) return { severity: 0.15, note: 'Over-sharpening artifacts detected' };
  if (haloRatio > 0.15) return { severity: 0.08, note: 'Slight sharpening halos' };
  return { severity: 0, note: '' };
}

// ── HDR artifact detection — tone-mapping compression ──
function detectHDRArtifacts(pa: PixelAnalysis): { severity: number; note: string } {
  // HDR tone-mapping compresses luminance range: low stddev + high histogram span
  let lumMean = 0;
  for (let i = 0; i < 256; i++) lumMean += i * pa.histogram[i];
  lumMean /= pa.pixelCount;
  let lumVar = 0;
  for (let i = 0; i < 256; i++) lumVar += pa.histogram[i] * (i - lumMean) ** 2;
  const lumStdDev = Math.sqrt(lumVar / pa.pixelCount);

  // Find p1 and p99 for dynamic range
  let cum = 0, p1 = 0, p99 = 255;
  for (let i = 0; i < 256; i++) {
    cum += pa.histogram[i];
    if (cum >= pa.pixelCount * 0.01 && p1 === 0) p1 = i;
    if (cum >= pa.pixelCount * 0.99) { p99 = i; break; }
  }
  const dynamicRange = p99 - p1;

  // Low luminance stddev (<15) + high dynamic range (>180) = aggressive tone-mapping
  if (lumStdDev < 15 && dynamicRange > 180) return { severity: 0.15, note: 'HDR tone-mapping artifacts' };
  if (lumStdDev < 20 && dynamicRange > 200) return { severity: 0.08, note: 'Slight HDR processing detected' };
  return { severity: 0, note: '' };
}

// ── Leading lines detection from Sobel angles ──
function detectLeadingLines(
  sobelMag: Float32Array, sobelAngle: Float32Array,
  width: number, height: number,
): { score: number; dominantDirections: number[] } {
  // Bin strong edge angles into 18 bins of 10 degrees each
  const bins = new Float64Array(18);
  let totalStrong = 0;
  for (let i = 0; i < sobelMag.length; i++) {
    if (sobelMag[i] > 40) {
      const deg = ((sobelAngle[i] * 180 / Math.PI) % 180 + 180) % 180; // 0-180
      const bin = Math.min(17, Math.floor(deg / 10));
      bins[bin]++;
      totalStrong++;
    }
  }
  if (totalStrong < 100) return { score: 0, dominantDirections: [] };

  // Find peaks (bins with >2x average count)
  const avgBin = totalStrong / 18;
  const peaks: number[] = [];
  for (let i = 0; i < 18; i++) {
    if (bins[i] > avgBin * 2) peaks.push(i * 10 + 5); // center of bin
  }

  if (peaks.length === 0) return { score: 0, dominantDirections: [] };

  // Check convergence: do lines converge toward center third?
  // Lines at angles ~45° or ~135° that pass through center = converging
  const centerThirdX = width / 3;
  const centerThirdY = height / 3;
  let convergingPixels = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = y * width + x;
      if (sobelMag[idx] > 40) {
        const deg = ((sobelAngle[idx] * 180 / Math.PI) % 180 + 180) % 180;
        const isPeakAngle = peaks.some(p => Math.abs(deg - p) < 15);
        if (isPeakAngle) {
          // Check if this edge is pointing toward center region
          const toCenterX = width / 2 - x;
          const toCenterY = height / 2 - y;
          const toCenter = Math.atan2(toCenterY, toCenterX) * 180 / Math.PI;
          const edgeDir = sobelAngle[idx] * 180 / Math.PI;
          const alignment = Math.abs(Math.cos((edgeDir - toCenter) * Math.PI / 180));
          if (alignment > 0.7 && x > centerThirdX && x < width - centerThirdX) {
            convergingPixels++;
          }
        }
      }
    }
  }

  const convergenceRatio = convergingPixels / Math.max(1, totalStrong);
  const score = Math.min(1, convergenceRatio * 5 + (peaks.length >= 2 ? 0.3 : 0));

  return { score: Math.min(1, score), dominantDirections: peaks };
}

// ── Negative space detection ──
function detectNegativeSpace(sobelMag: Float32Array, width: number, height: number): { ratio: number; score: number } {
  let quietPixels = 0;
  for (let i = 0; i < sobelMag.length; i++) {
    if (sobelMag[i] < 8) quietPixels++;
  }
  const ratio = quietPixels / (width * height);
  // Bell curve: sweet spot 0.45-0.70
  let score: number;
  if (ratio < 0.30) score = 0.3;       // Too cluttered
  else if (ratio < 0.45) score = 0.6;   // Slightly busy
  else if (ratio <= 0.70) score = 1.0;  // Sweet spot
  else if (ratio <= 0.85) score = 0.6;  // Quite empty
  else score = 0.3;                      // Too sparse
  return { ratio, score };
}

function scoreFaces(
  landmarker: any, canvas: HTMLCanvasElement, imgW: number, imgH: number,
  imageData?: Uint8ClampedArray,
): FaceResult {
  const empty: FaceResult = { score: 5.0, faceCount: 0, faceRelevance: 0, note: 'Portrait analysis unavailable', isSelfie: false, faceLandmarks: null, largestFaceRatio: 0, expressionDetractors: [], expressionIssues: [], smileIntensity: 0, eyeContact: 0.5, naturalness: 0.5 };
  if (!landmarker) return empty;

  try {
    const result = landmarker.detect(canvas);
    const faces = result.faceLandmarks ?? [];
    const faceCount = faces.length;

    if (faceCount === 0) return { score: 4.0, faceCount: 0, faceRelevance: 0, note: 'No faces detected', isSelfie: false, faceLandmarks: null, largestFaceRatio: 0, expressionDetractors: [], expressionIssues: [], smileIntensity: 0, eyeContact: 0.5, naturalness: 0.5 };

    // Compute face areas for face-to-frame ratio
    const faceAreas = faces.map((lms: any) => {
      if (!lms || lms.length < 10) return 0;
      const xs = lms.map((l: any) => l.x), ys = lms.map((l: any) => l.y);
      return (Math.max(...xs) - Math.min(...xs)) * (Math.max(...ys) - Math.min(...ys));
    });
    const largestFaceRatio = Math.max(...faceAreas);

    // Detect selfie context
    const isSelfie = detectSelfieContext(faceCount, largestFaceRatio, faces);

    // Score each face with elite expression analysis + advanced landmark signals
    const perFaceResults = faces.map((lms: any, idx: number) => {
      const bs = result.faceBlendshapes?.[idx]?.categories ?? [];
      const getBS = (name: string) => bs.find((b: any) => b.categoryName === name)?.score ?? 0;

      // Core expression analysis (duck face, symmetry, double chin, goofy, naturalness)
      const expr = analyzeExpression(getBS);
      let fs = expr.score;
      const allIssues = [...expr.issues];

      // Bonus budget: bad expression limits how much bonuses can recover the score.
      // Perfect expression = unlimited bonuses. Severe goofy = capped at 0.5.
      const bonusBudget = expr.goofySeverity >= 0.5 ? 0.5 :
                          expr.goofySeverity >= 0.3 ? 1.5 :
                          expr.goofySeverity > 0    ? 2.5 : Infinity;
      let bonusAccum = 0;
      const addBonus = (val: number): number => {
        if (val <= 0) return val; // Penalties always apply in full
        const allowed = Math.max(0, Math.min(val, bonusBudget - bonusAccum));
        bonusAccum += allowed;
        return allowed;
      };

      // Head pose analysis (yaw, pitch, roll from 3D landmarks)
      const headPose = analyzeHeadPose(lms);
      fs += addBonus(headPose.bonus);
      if (headPose.note) allIssues.push(headPose.note);

      // Jawline definition from landmark geometry
      const jawline = analyzeJawline(lms);
      fs += addBonus(jawline.bonus);

      // Eye openness granularity from landmark distances
      const eyeOpen = analyzeEyeOpenness(lms);
      fs += addBonus(eyeOpen.bonus);
      if (eyeOpen.note) allIssues.push(eyeOpen.note);

      // Skin uniformity in face region (even tone = good lighting on face)
      if (imageData) {
        const skin = analyzeSkinUniformity(imageData, imgW, imgH, lms);
        fs += addBonus(skin.bonus);
        if (skin.note) allIssues.push(skin.note);

        // Face lighting quality (side balance, brightness)
        const faceLighting = analyzeFaceLighting(imageData, imgW, imgH, lms);
        fs += addBonus(faceLighting.bonus);
        if (faceLighting.note) allIssues.push(faceLighting.note);
      }

      // Eye position rule-of-thirds bonus (also gated)
      if (lms?.length > 10) {
        const eyeY = (lms[468]?.y ?? lms[4]?.y ?? 0.5);
        const thirdsDeviation = Math.abs(eyeY - 0.333);
        const thirdsBonus = Math.max(0, (0.15 - thirdsDeviation) * 8);
        fs += addBonus(thirdsBonus);
      }

      return {
        score: Math.max(0, Math.min(10, fs)), issues: allIssues,
        suggestion: expr.topSuggestion, detractors: expr.detractors,
        goofySeverity: expr.goofySeverity,
        smileIntensity: expr.smileIntensity, eyeContact: expr.eyeContact, naturalness: expr.naturalness,
      };
    });

    const perFaceScores = perFaceResults.map((r: { score: number }) => r.score);
    const worstScore = Math.min(...perFaceScores);
    const avgScore = perFaceScores.reduce((a: number, b: number) => a + b, 0) / perFaceScores.length;
    let compositeScore = faceCount === 1 ? perFaceScores[0] : worstScore * 0.6 + avgScore * 0.4;

    // Apply face-to-frame bell curve multiplier (the critical fix)
    const framingMultiplier = faceToFrameMultiplier(largestFaceRatio, isSelfie);
    compositeScore *= framingMultiplier;

    const faceRelevance = framingMultiplier;

    // Build issue-specific diagnostic note
    const primaryResult = perFaceResults[0];
    let note: string;
    if (faceCount === 1) {
      if (primaryResult.issues.length > 0) {
        const issueText = primaryResult.issues.slice(0, 2).join(' · ');
        note = `${issueText} — ${primaryResult.suggestion}`;
      } else if (compositeScore >= 7) {
        note = 'Natural, well-composed portrait';
      } else if (compositeScore >= 5) {
        note = `Decent portrait — ${primaryResult.suggestion}`;
      } else if (framingMultiplier < 0.5) {
        note = 'Face too close to camera — step back for better framing';
      } else {
        note = `Portrait needs work — ${primaryResult.suggestion}`;
      }
    } else {
      note = `${faceCount} faces — ${worstScore < 5 ? 'one subject could improve' : 'good group shot'}`;
    }

    // Collect all expression detractors across faces (use worst severity per type)
    const detractorMap = new Map<string, number>();
    for (const r of perFaceResults) {
      for (const d of (r as any).detractors ?? []) {
        const existing = detractorMap.get(d.type) ?? 0;
        detractorMap.set(d.type, Math.max(existing, d.severity));
      }
    }
    const expressionDetractors = Array.from(detractorMap.entries()).map(([type, severity]) => ({ type, severity }));

    // Extract PRISM expression signals from primary face's expression analysis
    const primaryExpr = perFaceResults[0] as any;
    return {
      score: Math.max(0, Math.min(10, compositeScore)),
      faceCount,
      faceRelevance,
      note,
      isSelfie,
      faceLandmarks: faces,
      largestFaceRatio,
      expressionDetractors,
      expressionIssues: primaryResult?.issues ?? [],
      smileIntensity: primaryExpr?.smileIntensity ?? 0,
      eyeContact: primaryExpr?.eyeContact ?? 0.5,
      naturalness: primaryExpr?.naturalness ?? 0.5,
    };
  } catch {
    return { score: 0, faceCount: 0, faceRelevance: 0, note: '', isSelfie: false, faceLandmarks: null, largestFaceRatio: 0, expressionDetractors: [], expressionIssues: [], smileIntensity: 0, eyeContact: 0.5, naturalness: 0.5 };
  }
}

// ═══════════════════════════════════════════════════════════════
// SECTION 8: Score Transform v2 — piecewise linear from academic raw → consumer FRAME
// ═══════════════════════════════════════════════════════════════
// Raw analyzer scores stay academic-honest (1.0–10.0). This transform handles all
// consumer-friendliness at the OUTPUT layer. Real portraits always >= 4.0.
// Validated: 75/75 tests, 40 calibration personas, mean 6.98, stdev 1.6

// transformScore imported from @frame/utils (single source of truth: packages/utils/src/score-transform.ts)

// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// SECTION 8a-1: v2 Affirmation-First Diagnostics
// "What's working" + "Your biggest opportunity" + estimated improvement
// ═══════════════════════════════════════════════════════════════

interface DiagnosticV2 {
  strengths: string[];
  opportunity: string;
  opportunityDim: string;
  estimatedImproved: number;
}

const STRENGTH_DESCRIPTIONS: Record<string, (score: number) => string> = {
  portrait: (s) => s >= 8 ? 'Excellent portrait capture — natural presence and engagement'
    : s >= 7 ? 'Strong portrait — clear features and good expression'
    : 'Good portrait clarity and feature rendering',
  lighting: (s) => s >= 8 ? 'Beautiful lighting — dimensional quality with pleasing tones'
    : s >= 7 ? 'Good natural lighting with recognizable direction'
    : 'Adequate lighting — subject clearly visible',
  composition: (s) => s >= 8 ? 'Excellent framing — sharp eyes and masterful placement'
    : s >= 7 ? 'Well-composed with good framing and focus'
    : 'Solid composition with acceptable framing',
  scene: (s) => s >= 8 ? 'Background actively enhances — great depth and separation'
    : s >= 7 ? 'Clean background with intentional choice'
    : 'Neutral background that works without distraction',
};

const OPPORTUNITY_SUGGESTIONS: Record<string, string[]> = {
  portrait: [
    'Try a slight 3/4 angle — turning slightly adds dimension to your portrait',
    'A genuine laugh right before the shutter reads better than a held smile — have someone tell a joke',
    'Move the camera back a bit and zoom in — reduces lens distortion and flatters proportions',
    'Eye contact with the lens creates stronger connection — look directly at the camera',
  ],
  lighting: [
    'Natural window light from the side creates beautiful dimension — sit facing a window with the light at 45\u00b0',
    'Golden hour (1 hour before sunset) adds warmth and softens shadows — try shooting then',
    'Move out of direct overhead sun — open shade eliminates harsh shadows',
    'Turn off the flash and find existing light — flash flattens dimension',
  ],
  composition: [
    'Focus tap on the eyes before shooting — they\'re the anchor of every great portrait',
    'Try the rule of thirds — position yourself slightly off-center',
    'A little more headroom — leave some space above, crop tighter on sides',
    'Hold the camera at eye level or slightly above for the most flattering angle',
  ],
  scene: [
    'Step 3 feet away from the wall to create natural background separation and depth',
    'A simpler background lets you be the star — look for solid walls or nature',
    'Check behind you before shooting — watch for objects growing from your head',
    'A contextual background (caf\u00e9, park, travel spot) adds storytelling',
  ],
};

function generateDiagnosticV2(
  rawDims: { portrait: number | null; lighting: number; composition: number; scene: number },
  frameScore: number,
): DiagnosticV2 {
  const dims = [
    { key: 'portrait', score: rawDims.portrait ?? 0, hasScore: rawDims.portrait !== null },
    { key: 'lighting', score: rawDims.lighting, hasScore: true },
    { key: 'composition', score: rawDims.composition, hasScore: true },
    { key: 'scene', score: rawDims.scene, hasScore: true },
  ].filter(d => d.hasScore);

  // Top 2 dimensions = strengths
  const sorted = [...dims].sort((a, b) => b.score - a.score);
  const strengths = sorted.slice(0, 2).map(d => {
    const fn = STRENGTH_DESCRIPTIONS[d.key];
    return fn ? fn(d.score) : `Good ${d.key} quality`;
  });

  // Weakest = opportunity
  const weakest = sorted[sorted.length - 1];
  const suggestions = OPPORTUNITY_SUGGESTIONS[weakest.key] || [];
  const suggestion = suggestions[Math.floor(Math.random() * suggestions.length)] || `Improve ${weakest.key} for a better score`;

  // Estimated improvement: add 1.0-1.5 FRAME points, cap at 9.5
  const estimatedImproved = Math.min(9.5, frameScore + (weakest.score < 5 ? 1.5 : 1.0));

  return {
    strengths,
    opportunity: `${weakest.key.charAt(0).toUpperCase() + weakest.key.slice(1)} (${transformScore(weakest.score).toFixed(1)}) — ${suggestion}`,
    opportunityDim: weakest.key,
    estimatedImproved,
  };
}

// ═══════════════════════════════════════════════════════════════
// SECTION 8a-2: Smart Mode Suggestion — detects mode-photo mismatch
// ═══════════════════════════════════════════════════════════════

// ─── Gaze Dynamics from MediaPipe Iris Landmarks ──────────────
// Patent: Novel dimension — Gaze dynamics from iris-to-eye-corner ratios.
// MediaPipe provides iris landmarks (468-472 left, 473-477 right).

function computeGazeFromLandmarks(
  faceLandmarks: any[] | null,
  faceCount: number,
  eyeContact: number,
): GazeResult | null {
  if (!faceLandmarks || faceCount === 0) return null;

  const gazeDirections: GazeResult['gazeDirections'] = [];
  let totalDirectness = 0;
  let facesWithGaze = 0;

  for (let fi = 0; fi < faceLandmarks.length; fi++) {
    const face = faceLandmarks[fi];
    if (!face || face.length < 478) {
      gazeDirections.push({ faceIndex: fi, yaw: 0, pitch: 0, isEngaged: false });
      continue;
    }

    // Left eye: inner corner 133, outer corner 33, iris center 468
    // Right eye: inner corner 362, outer corner 263, iris center 473
    const leftInner = face[133], leftOuter = face[33], leftIris = face[468];
    const rightInner = face[362], rightOuter = face[263], rightIris = face[473];

    if (!leftIris || !rightIris) {
      gazeDirections.push({ faceIndex: fi, yaw: 0, pitch: 0, isEngaged: false });
      continue;
    }

    const leftEyeWidth = Math.abs(leftOuter.x - leftInner.x);
    const rightEyeWidth = Math.abs(rightOuter.x - rightInner.x);

    // Horizontal gaze: where is iris relative to eye corners? 0.5 = center
    const leftGazeX = leftEyeWidth > 0.001 ? (leftIris.x - leftOuter.x) / leftEyeWidth : 0.5;
    const rightGazeX = rightEyeWidth > 0.001 ? (rightIris.x - rightOuter.x) / rightEyeWidth : 0.5;

    // Vertical gaze (from iris Y relative to upper/lower eyelid)
    const leftGazeY = face[159] && face[145] ?
      (leftIris.y - face[159].y) / Math.max(0.001, face[145].y - face[159].y) : 0.5;
    const rightGazeY = face[386] && face[374] ?
      (rightIris.y - face[386].y) / Math.max(0.001, face[374].y - face[386].y) : 0.5;

    // Directness: distance from center (0.5) — closer = more direct gaze
    const leftDirectness = 1 - Math.abs(leftGazeX - 0.5) * 2;
    const rightDirectness = 1 - Math.abs(rightGazeX - 0.5) * 2;
    const horizontalDirectness = (leftDirectness + rightDirectness) / 2;

    const verticalDirectness = 1 - (Math.abs(leftGazeY - 0.4) + Math.abs(rightGazeY - 0.4));

    const directness = horizontalDirectness * 0.7 + Math.max(0, verticalDirectness) * 0.3;
    const isEngaged = directness > 0.6;

    // Convert to approximate yaw/pitch angles
    const yaw = (0.5 - (leftGazeX + rightGazeX) / 2) * 60; // ±30°
    const pitch = (0.5 - (leftGazeY + rightGazeY) / 2) * 40; // ±20°

    gazeDirections.push({ faceIndex: fi, yaw, pitch, isEngaged });
    totalDirectness += directness;
    facesWithGaze++;
  }

  if (facesWithGaze === 0) return null;

  const avgDirectness = totalDirectness / facesWithGaze;
  const engagedCount = gazeDirections.filter(g => g.isEngaged).length;

  return {
    primaryGazeAngle: Math.abs(gazeDirections[0]?.yaw ?? 0),
    eyeContactScore: avgDirectness * 10,
    groupEngagement: faceCount > 1 ? (engagedCount / faceCount) * 10 : avgDirectness * 10,
    blinkDetected: eyeContact < 0.2,
    squintLevel: Math.max(0, 1 - eyeContact) * 0.5,
    gazeDirections,
  };
}

// ─── Subject-Environment Harmony from Face Bbox ───────────────
// Patent: Novel dimension — Subject-environment harmony.
// Uses face bounding box to separate subject/background regions.
function computeSubjectEnvironmentSignals(
  faceLandmarks: any[] | null,
  faceCount: number,
  imageData: Uint8ClampedArray,
  w: number,
  h: number,
): { subjectHSV: { h: number; s: number; v: number }; bgHSV: { h: number; s: number; v: number }; faceArea: number } | null {
  if (!faceLandmarks || faceCount === 0) return null;
  const face = faceLandmarks[0];
  if (!face || face.length < 10) return null;

  // Get face bounding box
  const xs = face.map((l: any) => l.x);
  const ys = face.map((l: any) => l.y);
  const fMinX = Math.floor(Math.min(...xs) * w);
  const fMaxX = Math.ceil(Math.max(...xs) * w);
  const fMinY = Math.floor(Math.min(...ys) * h);
  const fMaxY = Math.ceil(Math.max(...ys) * h);

  // Sample subject and background average colors at stride-4
  let sR = 0, sG = 0, sB = 0, sN = 0;
  let bR = 0, bG = 0, bB = 0, bN = 0;
  const sampleStride = 4;

  for (let y = 0; y < h; y += sampleStride) {
    for (let x = 0; x < w; x += sampleStride) {
      const idx = (y * w + x) * 4;
      const inFace = x >= fMinX && x <= fMaxX && y >= fMinY && y <= fMaxY;
      if (inFace) {
        sR += imageData[idx]; sG += imageData[idx + 1]; sB += imageData[idx + 2]; sN++;
      } else {
        bR += imageData[idx]; bG += imageData[idx + 1]; bB += imageData[idx + 2]; bN++;
      }
    }
  }

  if (sN === 0 || bN === 0) return null;

  const subjectHSV = rgbToHsv(sR / sN, sG / sN, sB / sN);
  const bgHSV = rgbToHsv(bR / bN, bG / bN, bB / bN);
  const faceArea = ((fMaxX - fMinX) * (fMaxY - fMinY)) / (w * h);

  return { subjectHSV, bgHSV, faceArea };
}

interface ModeSuggestion {
  suggestedMode: string;
  reason: string;
  estimatedImprovement: string;
}

function suggestBetterMode(
  modeId: string,
  faceCount: number,
  isSelfie: boolean,
  portrait: number | null,
  scene: number,
  landmarkTier: number,
): ModeSuggestion | null {
  // Rule 1: Selfie in Travel/RealEstate → suggest Social
  if (isSelfie && (modeId === 'travel' || modeId === 'realEstate')) {
    return {
      suggestedMode: 'social',
      reason: 'This looks like a portrait — Social mode emphasizes how you look',
      estimatedImprovement: 'better portrait weighting',
    };
  }

  // Rule 2: No faces in Dating/Social/Professional → suggest Travel or Balanced
  if (faceCount === 0 && ['social', 'dating'].includes(modeId)) {
    if (landmarkTier > 0 || scene > 6) {
      return {
        suggestedMode: 'travel',
        reason: 'No people detected — Travel mode highlights scenes and landmarks',
        estimatedImprovement: 'better scene weighting',
      };
    }
    return {
      suggestedMode: 'balanced',
      reason: 'No people detected — Balanced mode gives equal weight to all dimensions',
      estimatedImprovement: 'more balanced scoring',
    };
  }

  // Rule 3: Group shot (3+ faces) in Dating/Social → suggest Event
  if (faceCount >= 3 && ['social', 'dating'].includes(modeId)) {
    return {
      suggestedMode: 'event',
      reason: 'Group photo detected — Event mode is lenient with crowds and flash',
      estimatedImprovement: 'reduced crowd penalty',
    };
  }

  // Rule 4: Faces in RealEstate → suggest Balanced
  if (faceCount > 0 && modeId === 'realEstate') {
    return {
      suggestedMode: 'balanced',
      reason: 'People detected — Property mode ignores portrait quality entirely',
      estimatedImprovement: 'portrait score included',
    };
  }

  // Rule 5: Landmark in non-Travel → suggest Travel
  if (landmarkTier >= 2 && modeId !== 'travel' && modeId !== 'balanced') {
    return {
      suggestedMode: 'travel',
      reason: 'Landmark detected — Travel mode boosts scene interest with landmark spikes',
      estimatedImprovement: 'landmark bonus applied',
    };
  }

  // Rule 6: Strong portrait, weak scene in Travel → suggest Professional
  if (portrait !== null && portrait > 7 && scene < 4 && modeId === 'travel') {
    return {
      suggestedMode: 'professional',
      reason: 'Great portrait quality — Professional mode emphasizes portrait composition',
      estimatedImprovement: 'higher portrait weight',
    };
  }

  return null;
}

// ═══════════════════════════════════════════════════════════════
// SECTION 8b: Strict Scrutiny Gate — pre-output validation
// Checks for contradictions and applies floor/ceiling corrections.
// ═══════════════════════════════════════════════════════════════

function strictScrutiny(
  portrait: number | null, lighting: number, composition: number, scene: number,
  faceResult: FaceResult,
): { portrait: number | null; lighting: number; composition: number; scene: number; scrutinyNotes: string[] } {
  const notes: string[] = [];
  let p = portrait, l = lighting, c = composition, s = scene;

  // Rule 1: Bad expression → portrait capped at 5.5 (natural expressions like smirks shouldn't demolish scores)
  if (p !== null && faceResult.expressionDetractors.some(d => d.type === 'bad_expression')) {
    if (p > 5.5) { p = 5.5; notes.push('Portrait capped: bad expression'); }
  }

  // Rule 2: Closed eyes → portrait capped at 3.0
  if (p !== null && faceResult.expressionDetractors.some(d => d.type === 'closed_eyes' && d.severity >= 0.25)) {
    if (p > 3.0) { p = 3.0; notes.push('Portrait capped: eyes closed'); }
  }

  // Rule 3: Selfie with bad expression → scene capped at 5.0
  if (faceResult.isSelfie && faceResult.expressionDetractors.some(d => d.type === 'bad_expression')) {
    if (s > 5.0) { s = 5.0; notes.push('Scene capped: selfie with bad expression'); }
  }

  // Rule 4: Cross-dimension coherence — poor selfie portrait caps composition
  if (faceResult.isSelfie && p !== null && p < 3.0 && c > 6.0) {
    c = 6.0; notes.push('Composition capped: poor portrait in selfie');
  }

  // Rule 5: No dimension above 9.0 unless all others are above 5.0
  const allScores = [p, l, c, s].filter((v): v is number => v !== null);
  const minScore = Math.min(...allScores);
  if (minScore < 5.0) {
    const cap = 9.0;
    if (p !== null && p > cap) { p = cap; notes.push('Portrait capped: dimension imbalance'); }
    if (l > cap) { l = cap; notes.push('Lighting capped: dimension imbalance'); }
    if (c > cap) { c = cap; notes.push('Composition capped: dimension imbalance'); }
    if (s > cap) { s = cap; notes.push('Scene capped: dimension imbalance'); }
  }

  return { portrait: p, lighting: l, composition: c, scene: s, scrutinyNotes: notes };
}

// ═══════════════════════════════════════════════════════════════
// SECTION 8c: Mode Calibration — data-driven per-mode adjustments
// Centralizes mode-specific bonuses/penalties into a single config table
// ═══════════════════════════════════════════════════════════════

interface ModeCalibrationResult {
  lightingAdj: number;
  compositionAdj: number;
  sceneAdj: number;
  portraitAdj: number;
  suppressPortrait: boolean;
  notes: Array<{ type: string; value: number }>;
}

interface ModeCalibrationInput {
  warmthRatio: number;
  contrastStdDev: number;
  avgSat: number;
  faceCount: number;
  isSelfie: boolean;
  isLandscape: boolean;
}

interface ModeCalibrationConfig {
  goldenHourBonus: number;
  warmthBonus: { enabled: boolean; warm: number; hot: number };
  saturationSweetSpot: { enabled: boolean; low: number; high: number; bonus: number; overPenalty: number };
  tiltMultiplier: number;
  selfieContextPenalty: number;
  suppressPortraitNoFace: boolean;
  iWasThereBonus: boolean;
  hdrTolerant: boolean;
  neutralLightingPreferred: boolean;
  exprStrictness: 'relaxed' | 'standard' | 'strict';
}

const MODE_CALIBRATIONS: Record<string, Partial<ModeCalibrationConfig>> = {
  balanced: {},
  social: {
    warmthBonus: { enabled: true, warm: 1.0, hot: 0.4 },
    selfieContextPenalty: 0,
  },
  instagram: {
    warmthBonus: { enabled: true, warm: 0.6, hot: 0.2 },
    saturationSweetSpot: { enabled: true, low: 60, high: 140, bonus: 0.8, overPenalty: 0.5 },
  },
  travel: {
    goldenHourBonus: 1.5,
    suppressPortraitNoFace: true,
    iWasThereBonus: true,
  },
  realEstate: {
    tiltMultiplier: 3.0,
    hdrTolerant: true,
    suppressPortraitNoFace: true,
  },
  professional: {
    selfieContextPenalty: 0.25,
    exprStrictness: 'strict',
    neutralLightingPreferred: true,
    tiltMultiplier: 1.5,
  },
  event: {
    warmthBonus: { enabled: true, warm: 0.6, hot: 0.2 },
    tiltMultiplier: 0.7,
    suppressPortraitNoFace: true,
  },
  sports: {
    tiltMultiplier: 0.5,
    suppressPortraitNoFace: true,
  },
};

const DEFAULT_CALIBRATION: ModeCalibrationConfig = {
  goldenHourBonus: 0,
  warmthBonus: { enabled: false, warm: 0, hot: 0 },
  saturationSweetSpot: { enabled: false, low: 60, high: 140, bonus: 0, overPenalty: 0 },
  tiltMultiplier: 1.0,
  selfieContextPenalty: 0,
  suppressPortraitNoFace: false,
  iWasThereBonus: false,
  hdrTolerant: false,
  neutralLightingPreferred: false,
  exprStrictness: 'standard',
};

function applyModeCalibration(modeId: string, input: ModeCalibrationInput): ModeCalibrationResult {
  const cfg: ModeCalibrationConfig = { ...DEFAULT_CALIBRATION, ...(MODE_CALIBRATIONS[modeId] || {}) };
  // Merge nested objects properly
  if (MODE_CALIBRATIONS[modeId]?.warmthBonus) {
    cfg.warmthBonus = { ...DEFAULT_CALIBRATION.warmthBonus, ...MODE_CALIBRATIONS[modeId]!.warmthBonus };
  }
  if (MODE_CALIBRATIONS[modeId]?.saturationSweetSpot) {
    cfg.saturationSweetSpot = { ...DEFAULT_CALIBRATION.saturationSweetSpot, ...MODE_CALIBRATIONS[modeId]!.saturationSweetSpot };
  }

  const result: ModeCalibrationResult = { lightingAdj: 0, compositionAdj: 0, sceneAdj: 0, portraitAdj: 0, suppressPortrait: false, notes: [] };

  // Golden hour bonus (warmthRatio > 0.60 as proxy)
  if (cfg.goldenHourBonus > 0 && input.warmthRatio > 0.60) {
    const intensity = Math.min(1, (input.warmthRatio - 0.60) / 0.20);
    const bonus = cfg.goldenHourBonus * intensity;
    result.lightingAdj += bonus;
    result.sceneAdj += bonus * 0.5;
    result.notes.push({ type: 'goldenHour', value: bonus });
  }

  // Warmth bonus
  if (cfg.warmthBonus.enabled) {
    if (input.warmthRatio > 0.55 && input.warmthRatio <= 0.70) {
      result.lightingAdj += cfg.warmthBonus.warm;
      result.notes.push({ type: 'warmth', value: cfg.warmthBonus.warm });
    } else if (input.warmthRatio > 0.70) {
      result.lightingAdj += cfg.warmthBonus.hot;
      result.notes.push({ type: 'warmthHot', value: cfg.warmthBonus.hot });
    }
  }

  // Saturation sweet spot
  if (cfg.saturationSweetSpot.enabled) {
    const sat = input.avgSat;
    if (sat >= cfg.saturationSweetSpot.low && sat <= cfg.saturationSweetSpot.high) {
      result.sceneAdj += cfg.saturationSweetSpot.bonus;
      result.notes.push({ type: 'saturationSweetSpot', value: cfg.saturationSweetSpot.bonus });
    } else if (sat > 180) {
      result.sceneAdj -= cfg.saturationSweetSpot.overPenalty;
      result.notes.push({ type: 'oversatPenalty', value: -cfg.saturationSweetSpot.overPenalty });
    }
  }

  // Selfie context penalty (e.g., professional mode penalizes selfies)
  if (cfg.selfieContextPenalty > 0 && input.isSelfie) {
    result.portraitAdj -= cfg.selfieContextPenalty * 10; // Convert to 0-10 scale
    result.notes.push({ type: 'selfiePenalty', value: -cfg.selfieContextPenalty });
  }

  // Suppress portrait on no-face (travel, real estate, event, sports)
  if (cfg.suppressPortraitNoFace && input.faceCount === 0) {
    result.suppressPortrait = true;
  }

  // "I was there" bonus — face + landscape scene = travel selfie with landmark
  if (cfg.iWasThereBonus && input.faceCount > 0 && input.faceCount <= 2) {
    result.sceneAdj += 0.5;
    result.notes.push({ type: 'iWasThere', value: 0.5 });
  }

  // Neutral lighting preference (professional mode)
  if (cfg.neutralLightingPreferred && input.warmthRatio >= 0.35 && input.warmthRatio <= 0.55) {
    result.lightingAdj += 0.6;
    result.notes.push({ type: 'neutralLight', value: 0.6 });
  }

  // Background complexity penalty for social/dating mode
  if (modeId === 'social' && input.faceCount > 0) {
    const bgComplexity = input.contrastStdDev / 70;
    if (bgComplexity > 0.45) {
      result.compositionAdj -= 1.0;
      result.notes.push({ type: 'busyBg', value: -1.0 });
    } else if (bgComplexity < 0.15) {
      result.compositionAdj += 0.8;
      result.notes.push({ type: 'cleanBg', value: 0.8 });
    }
  }

  // Professional close-up penalty
  if (modeId === 'professional' && input.isSelfie && input.faceCount === 1) {
    result.portraitAdj -= 1.0;
    result.notes.push({ type: 'closeupPenalty', value: -1.0 });
  }

  // Real estate: suppress portrait always (weight is 0 but prevents diagnostic noise)
  if (modeId === 'realEstate') {
    result.suppressPortrait = true;
  }

  return result;
}

// ═══════════════════════════════════════════════════════════════
// SECTION 9: Public API — Memory-safe batch processing
// Sequential + canvas cleanup + GC yield (fixes Safari crash)
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// SECTION 9b: EXIF Metadata — contextual scoring bonuses
// ═══════════════════════════════════════════════════════════════

interface ExifData {
  focalLength: number | null;
  aperture: number | null;
  iso: number | null;
  shutterSpeed: number | null;
  make: string | null;
  model: string | null;
  dateTime: Date | null;
  orientation: number | null;
  latitude: number | null;
  longitude: number | null;
}

async function extractExif(file: File): Promise<ExifData | null> {
  try {
    const exifr = await import('exifr');
    const parsed = await exifr.parse(file, {
      pick: ['FocalLength', 'FNumber', 'ISO', 'ExposureTime', 'Make', 'Model', 'DateTimeOriginal', 'Orientation', 'latitude', 'longitude'],
    });
    if (!parsed) return null;
    return {
      focalLength: parsed.FocalLength ?? null,
      aperture: parsed.FNumber ?? null,
      iso: parsed.ISO ?? null,
      shutterSpeed: parsed.ExposureTime ?? null,
      make: parsed.Make ?? null,
      model: parsed.Model ?? null,
      dateTime: parsed.DateTimeOriginal ?? null,
      orientation: parsed.Orientation ?? null,
      latitude: parsed.latitude ?? null,
      longitude: parsed.longitude ?? null,
    };
  } catch {
    return null;
  }
}

function scoreExifContext(exif: ExifData | null, faceCount: number, noiseDetected: boolean): { bonus: number; notes: string[] } {
  if (!exif) return { bonus: 0, notes: [] };
  let bonus = 0;
  const notes: string[] = [];

  // Focal length appropriateness
  if (exif.focalLength) {
    if (faceCount > 0 && exif.focalLength >= 50 && exif.focalLength <= 85) {
      bonus += 0.3; // Portrait sweet spot
      notes.push(`Portrait lens (${exif.focalLength}mm)`);
    } else if (faceCount > 0 && exif.focalLength < 24) {
      bonus -= 0.3; // Wide angle distortion with faces
      notes.push(`Wide angle may distort faces (${exif.focalLength}mm)`);
    } else if (faceCount === 0 && exif.focalLength >= 16 && exif.focalLength <= 35) {
      bonus += 0.2; // Landscape sweet spot
    }
  }

  // ISO noise cross-reference
  if (exif.iso) {
    if (exif.iso > 3200 && noiseDetected) {
      notes.push(`High ISO (${exif.iso}) — noise expected`);
    } else if (exif.iso < 400 && noiseDetected) {
      bonus += 0.1; // Noise is likely compression artifact, not real noise
      notes.push('Low ISO — noise may be compression artifact');
    }
  }

  return { bonus, notes };
}

export interface PrismState {
  enabled: boolean;
  preferenceVector: PrismPreferenceVector;
  ownerBaseline: PrismOwnerBaseline | null;
}

export interface WebScoringOptions {
  weights: WeightProfile;
  modeId?: string; // e.g. 'social', 'professional', 'travel', 'balanced', 'instagram', 'realEstate'
  onProgress?: (current: number, total: number) => void;
  prismState?: PrismState;
  novelHints?: Partial<Record<string, number>>; // from vibe parser — adjusts novel dimension weights
  debug?: boolean; // When true, populates _debug field on each PhotoScore with intermediate values
}

export async function scorePhotosWeb(
  imageFiles: File[],
  options: WebScoringOptions,
): Promise<PhotoScore[]> {
  const [nimaAvailable, landmarker] = await Promise.all([
    loadNIMA(),
    loadFaceLandmarker(),
  ]);

  const scores: PhotoScore[] = [];
  const hashMap: { id: string; hash: bigint; compositeScore: number }[] = [];
  const blobUrls: string[] = [];
  const allSocialSignals: SocialSignals[] = [];

  for (let i = 0; i < imageFiles.length; i++) {
    const file = imageFiles[i];
    const start = performance.now();
    let canvas: HTMLCanvasElement | null = null;
    let thumbnailUrl: string | null = null;

    try {
      const [bitmap, exifData] = await Promise.all([
        createImageBitmap(file, { imageOrientation: 'from-image' } as any),
        extractExif(file),
      ]);

      canvas = document.createElement('canvas');
      const scale = Math.min(1, 800 / Math.max(bitmap.width, bitmap.height));
      const w = Math.round(bitmap.width * scale);
      const h = Math.round(bitmap.height * scale);
      canvas.width = w;
      canvas.height = h;
      // White background fill — prevents PNG transparency from confusing face detection
      const drawCtx = canvas.getContext('2d')!;
      drawCtx.fillStyle = '#FFFFFF';
      drawCtx.fillRect(0, 0, w, h);
      drawCtx.drawImage(bitmap, 0, 0, w, h);
      bitmap.close();

      const imageData = canvas.getContext('2d')!.getImageData(0, 0, w, h);
      const pa = singlePassAnalysis(imageData.data, w, h);

      // ── Non-photo content classification — detect but still score normally ──
      const nonPhotoCheck = detectNonPhotographicContent(imageData.data, w, h, pa);

      // Compute perceptual hash for near-duplicate detection
      const imgHash = computeAverageHash(imageData.data, w, h);

      const isScreenshot = pa.screenshotConfidence > 0.6;

      const lightingResult = scoreLighting(pa);
      let faceResult = scoreFaces(landmarker, canvas, w, h, imageData.data);

      // Skin-tone fallback: when face detection fails, check for skin-colored regions
      if (faceResult.faceCount === 0) {
        const skinRatio = detectSkinToneRegions(imageData.data, w, h);
        if (skinRatio > 0.10) {
          faceResult = { ...faceResult, score: 4.5, faceCount: 1, note: 'Skin tone fallback — face not detected by MediaPipe' };
        }
      }

      const compositionResult = scoreComposition(pa, w, h, {
        isSelfie: faceResult.isSelfie,
        faceLandmarks: faceResult.faceLandmarks,
      }, options.modeId);
      const nimaScore = nimaAvailable ? await scoreWithNIMA(canvas) : -1;
      const sceneResult = scoreSceneInterest(pa, nimaScore);

      // ── Formal detractor detection (multiplicative on composite) ──
      const blurDetector = detectBlur(pa, w, h);
      const exposureDetector = detectSevereExposure(pa);
      const noiseDetector = detectNoise(pa);
      const oversatDetector = detectOversaturation(imageData.data, w * h);
      const oversharpenDetector = detectOversharpening(pa, w, h);
      const hdrDetector = detectHDRArtifacts(pa);

      // v2: Use raw academic-honest scores — transform happens at output layer
      let lightingRaw = lightingResult.score;
      let compositionRaw = compositionResult.score;
      // Subject isolation bonus for multi-person scenes (event/sports)
      if (faceResult.faceCount >= 2 && faceResult.faceLandmarks &&
          (options.modeId === 'event' || options.modeId === 'sports')) {
        const isoBonus = subjectIsolationBonus(faceResult.faceLandmarks, faceResult.faceCount);
        compositionRaw = Math.min(10, compositionRaw + isoBonus);
      }
      let sceneRaw = sceneResult.score;
      // Scene floor for portraits — the person IS the scene; a plain wall shouldn't tank the score
      if (faceResult.faceCount > 0 && sceneRaw < 3.5) {
        sceneRaw = 3.5;
      }
      let portraitRaw = faceResult.faceCount > 0 ? faceResult.score : null;

      // EXIF contextual bonus
      const exifContext = scoreExifContext(exifData, faceResult.faceCount, noiseDetector.severity > 0);
      if (exifContext.bonus !== 0 && portraitRaw !== null) {
        portraitRaw = Math.max(0, Math.min(10, portraitRaw + exifContext.bonus));
      }

      // Landmark detection from EXIF GPS (Patent provisional #2)
      let detectedLandmarkTier = LandmarkTier.Ordinary;
      let detectedLandmarkName = '';
      if (exifData?.latitude != null && exifData?.longitude != null) {
        const landmark = detectLandmarkFromGPS(exifData.latitude, exifData.longitude);
        detectedLandmarkTier = landmark.tier;
        detectedLandmarkName = landmark.name;
      }

      // Apply landmark spike to scene interest
      if (detectedLandmarkTier > LandmarkTier.Ordinary) {
        sceneRaw = applyLandmarkSpike(
          sceneRaw, detectedLandmarkTier, options.weights?.landmarkSpikeStrength ?? 1.0,
        );
      }

      // Selfie scene cap — only cap when scene is genuinely uninteresting (no landmark, low colorfulness)
      if (faceResult.isSelfie && sceneRaw > 5.0 &&
          detectedLandmarkTier === LandmarkTier.Ordinary && pa.colorfulness < 0.3) {
        sceneRaw = 5.0;
      }

      // ── Mode-specific scoring bonuses ──
      const modeSpecificBonuses: Array<{ type: string; value: number }> = [];

      // Item 4: Graduated warmth bonus for Social mode (patent provisional v3 values)
      if (options.modeId === 'social' && lightingResult.kelvin > 0) {
        let warmthBonus = 0;
        if (lightingResult.kelvin >= 3500 && lightingResult.kelvin <= 5500) {
          warmthBonus = 0.8; // Sweet spot — inviting warmth
        } else if (lightingResult.kelvin >= 2500 && lightingResult.kelvin < 3500) {
          warmthBonus = 0.4; // Warm but slightly too amber
        } else if (lightingResult.kelvin > 5500 && lightingResult.kelvin <= 6500) {
          warmthBonus = 0.2; // Cool-neutral, slight clarity bonus
        }
        if (warmthBonus > 0) {
          lightingRaw = Math.min(10, lightingRaw + warmthBonus);
          modeSpecificBonuses.push({ type: 'warmth', value: warmthBonus });
        }
      }

      // Item 5: Saturation sweet spot for Instagram mode
      if (options.modeId === 'instagram' && pa.colorfulness >= 0.35 && pa.colorfulness <= 0.65) {
        const satBonus = 0.5;
        sceneRaw = Math.min(10, sceneRaw + satBonus);
        modeSpecificBonuses.push({ type: 'saturationSweetSpot', value: satBonus });
      }

      // Item 6: Background intentionality bonus for Social mode (face-masked edge density)
      if (options.modeId === 'social' && faceResult.faceCount > 0 && faceResult.faceLandmarks) {
        const bgIntentBonus = computeBackgroundIntentionality(
          pa.sobelMag, w, h, faceResult.faceLandmarks,
        );
        if (bgIntentBonus > 0) {
          compositionRaw = Math.min(10, compositionRaw + bgIntentBonus);
          modeSpecificBonuses.push({ type: 'backgroundIntentionality', value: bgIntentBonus });
        }
      }

      // ── Mode Calibration System — data-driven per-mode adjustments ──
      const modeCalibration = applyModeCalibration(options.modeId || 'balanced', {
        warmthRatio: lightingResult.warmthRatio,
        contrastStdDev: lightingResult.contrastStdDev,
        avgSat: pa.avgSat,
        faceCount: faceResult.faceCount,
        isSelfie: faceResult.isSelfie,
        isLandscape: faceResult.faceCount === 0,
      });
      lightingRaw = Math.max(0, Math.min(10, lightingRaw + modeCalibration.lightingAdj));
      compositionRaw = Math.max(0, Math.min(10, compositionRaw + modeCalibration.compositionAdj));
      sceneRaw = Math.max(0, Math.min(10, sceneRaw + modeCalibration.sceneAdj));
      if (portraitRaw !== null) {
        portraitRaw = Math.max(0, Math.min(10, portraitRaw + modeCalibration.portraitAdj));
      }
      if (modeCalibration.suppressPortrait && faceResult.faceCount === 0) {
        portraitRaw = null;
      }
      modeCalibration.notes.forEach(n => modeSpecificBonuses.push(n));

      // Screenshot/meme detection — lower thresholds to catch more non-photos
      if (pa.screenshotConfidence > 0.7) {
        lightingRaw = Math.min(lightingRaw, 1.5);
        compositionRaw = Math.min(compositionRaw, 1.5);
        sceneRaw = Math.min(sceneRaw, 1.5);
        if (portraitRaw !== null) portraitRaw = Math.min(portraitRaw, 1.5);
      } else if (pa.screenshotConfidence > 0.4) {
        const penalty = pa.screenshotConfidence * 4.0;
        lightingRaw = Math.max(0, lightingRaw - penalty);
        compositionRaw = Math.max(0, compositionRaw - penalty);
        sceneRaw = Math.max(0, sceneRaw - penalty);
        if (portraitRaw !== null) portraitRaw = Math.max(0, portraitRaw - penalty);
      }

      // ── 1J: Strict Scrutiny Gate — cross-dimension validation ──
      const scrutinized = strictScrutiny(portraitRaw, lightingRaw, compositionRaw, sceneRaw, faceResult);
      // Raw dimensions for composite computation (academic-honest scale)
      const rawDimensions: DimensionScores = {
        portraitComposition: scrutinized.portrait,
        lighting: scrutinized.lighting,
        composition: scrutinized.composition,
        sceneInterest: scrutinized.scene,
        sceneInterestSpiked: scrutinized.scene,
      };
      // v2: Transformed dimensions for display (consumer-friendly FRAME scale)
      const dimensions: DimensionScores = {
        portraitComposition: scrutinized.portrait !== null ? transformScore(scrutinized.portrait) : null,
        lighting: transformScore(scrutinized.lighting),
        composition: transformScore(scrutinized.composition),
        sceneInterest: transformScore(scrutinized.scene),
        sceneInterestSpiked: transformScore(scrutinized.scene),
      };

      // Mode-aware crowd penalty: disabled for event/sports modes where crowds are expected
      const crowdPenalty = options.weights.crowdPenaltyEnabled !== false
        ? computeCrowdPenalty(faceResult.faceCount)
        : 0;

      // Mode-aware blur tolerance: reduce severity for sports/action modes
      // Directional (motion) blur gets 75% reduction; uniform blur gets 50% reduction
      let effectiveBlur = blurDetector;
      if (options.weights.motionBlurTolerant && blurDetector.severity > 0) {
        const reduction = blurDetector.isDirectional ? 0.75 : 0.50;
        effectiveBlur = { ...blurDetector, severity: Math.max(0, blurDetector.severity * (1 - reduction)) };
      }

      // Mode-aware flash tolerance: reduce exposure penalty for event modes where flash is expected
      let effectiveExposure = exposureDetector;
      if (options.weights.flashTolerant && exposureDetector.severity > 0) {
        effectiveExposure = { severity: Math.max(0, exposureDetector.severity * 0.5), note: exposureDetector.note };
      }

      // v2: Subtractive detractor penalties with diminishing returns.
      // First detractor at full weight, subsequent at 60%.
      // Penalty amounts in RAW SCORE POINTS (not percentages).
      // TODO: Migrate to computeDetractorPenalty() from @frame/utils after recalibrating
      // DETRACTOR_BASE_PENALTIES to match these inline scale factors (severity/0.15 normalization differs).
      const activeDetractors: { type: string; penalty: number }[] = [];

      // Convert severity (0-1 fractional) to raw-score-point penalties using v2 detractor table
      if (crowdPenalty > 0) activeDetractors.push({ type: 'crowd', penalty: crowdPenalty * 3.0 });
      for (const d of faceResult.expressionDetractors) {
        const penaltyMap: Record<string, number> = { bad_expression: 1.0, closed_eyes: 1.5 };
        const basePenalty = penaltyMap[d.type] ?? 0.5;
        // Scale: severity 0.15 (typical) → full basePenalty. Higher severity → proportionally more.
        activeDetractors.push({ type: d.type, penalty: basePenalty * (d.severity / 0.15) });
      }
      if (effectiveBlur.severity > 0) activeDetractors.push({ type: 'motion_blur', penalty: effectiveBlur.severity * 5.0 }); // 0.40 → 2.0
      if (effectiveExposure.severity > 0) activeDetractors.push({ type: 'exposure_severe', penalty: effectiveExposure.severity * 6.0 }); // 0.25 → 1.5
      if (noiseDetector.severity > 0) activeDetractors.push({ type: 'low_resolution', penalty: noiseDetector.severity * 3.2 }); // 0.25 → 0.8
      if (oversatDetector.severity > 0) activeDetractors.push({ type: 'heavy_filter', penalty: oversatDetector.severity * 2.0 }); // 0.20 → 0.4
      if (oversharpenDetector.severity > 0) activeDetractors.push({ type: 'oversharpening', penalty: oversharpenDetector.severity * 2.0 });
      if (hdrDetector.severity > 0) activeDetractors.push({ type: 'hdr', penalty: hdrDetector.severity * 2.0 });

      // Sort by penalty descending — worst detractor first (gets full weight)
      activeDetractors.sort((a, b) => b.penalty - a.penalty);

      // Apply with diminishing returns: first at 100%, rest at 60%
      let totalSubtraction = 0;
      const detractorNotes: string[] = [];
      activeDetractors.forEach((d, i) => {
        const effective = i === 0 ? d.penalty : d.penalty * 0.6;
        totalSubtraction += effective;
        detractorNotes.push(`${d.type}: -${effective.toFixed(1)} pts`);
      });
      const totalDetractorPenalty = totalSubtraction; // now in raw score points

      // 1E: Selfie-aware dynamic weight boost — portrait dominates for selfies
      let effectiveWeights = options.weights;
      if (faceResult.isSelfie && faceResult.faceCount > 0) {
        const boost = 0.15;
        const newPortrait = Math.min(0.55, options.weights.portraitWeight + boost);
        const excess = newPortrait - options.weights.portraitWeight;
        const otherTotal = options.weights.lightingWeight + options.weights.compositionWeight + options.weights.sceneWeight;
        if (otherTotal > 0 && excess > 0) {
          effectiveWeights = {
            ...options.weights,
            portraitWeight: newPortrait,
            lightingWeight: options.weights.lightingWeight - excess * (options.weights.lightingWeight / otherTotal),
            compositionWeight: options.weights.compositionWeight - excess * (options.weights.compositionWeight / otherTotal),
            sceneWeight: options.weights.sceneWeight - excess * (options.weights.sceneWeight / otherTotal),
          };
        }
      }

      // v2: Use raw dimensions for composite, subtractive detractor penalty
      const isPortrait = !nonPhotoCheck.isNonPhoto || nonPhotoCheck.category === 'photo';
      const { rawComposite, finalComposite: penalizedRaw } = computeComposite(
        rawDimensions, effectiveWeights, totalDetractorPenalty, isPortrait,
      );
      // v2: Transform penalized raw composite to consumer-friendly FRAME score
      const frameScore = isPortrait ? transformScore(penalizedRaw) : Math.round(penalizedRaw * 10) / 10;

      thumbnailUrl = URL.createObjectURL(file);
      blobUrls.push(thumbnailUrl);

      const notes: Record<string, string> = {};
      if (detectedLandmarkTier > LandmarkTier.Ordinary) {
        notes._landmark = `${detectedLandmarkName} (${LandmarkTier[detectedLandmarkTier]})`;
      }
      if (nonPhotoCheck.isNonPhoto) {
        notes._tag = nonPhotoCheck.category;
        notes._nonPhoto = `${nonPhotoCheck.category}: ${nonPhotoCheck.reason}`;
      }
      if (lightingResult.note) notes.lighting = lightingResult.note;
      if (compositionResult.note) notes.composition = compositionResult.note;
      if (sceneResult.note) notes.sceneInterest = sceneResult.note;
      if (faceResult.note) notes.portraitComposition = faceResult.note;
      // 1M: Include expression issues for chat context
      if (faceResult.expressionIssues.length > 0) {
        notes._expression = faceResult.expressionIssues.join(', ');
      }
      if (scrutinized.scrutinyNotes.length > 0) {
        notes._scrutiny = scrutinized.scrutinyNotes.join(', ');
      }
      if (effectiveBlur.note) notes._blur = effectiveBlur.note;
      if (exposureDetector.note) notes._exposure = exposureDetector.note;
      if (noiseDetector.note) notes._noise = noiseDetector.note;
      if (oversatDetector.note) notes._oversaturation = oversatDetector.note;
      if (oversharpenDetector.note) notes._oversharpening = oversharpenDetector.note;
      if (hdrDetector.note) notes._hdr = hdrDetector.note;
      if (detractorNotes.length > 0) notes._detractors = detractorNotes.join(', ');
      if (isScreenshot) notes._screenshot = `Non-photo detected (${Math.round(pa.screenshotConfidence * 100)}% confidence)`;
      if (exifContext.notes.length > 0) notes._exif = exifContext.notes.join(', ');

      // PRISM: Extract aesthetic properties from already-computed signals
      const prismAesthetics = extractAestheticProperties({
        avgSat: pa.avgSat,
        warmthRatio: lightingResult.warmthRatio,
        contrastStdDev: lightingResult.contrastStdDev,
        thirdsScore: compositionResult.thirdsScore,
        symmetryScore: compositionResult.symmetryScore,
        bgComplexity: compositionResult.bgComplexity,
        smileIntensity: faceResult.smileIntensity,
        eyeContact: faceResult.eyeContact,
        naturalness: faceResult.naturalness,
        largestFaceRatio: faceResult.largestFaceRatio,
        faceCount: faceResult.faceCount,
        isSelfie: faceResult.isSelfie,
        portraitScore: portraitRaw,
        lightingScore: lightingRaw,
        compositionScore: compositionRaw,
        sceneScore: sceneRaw,
        compositeScore: frameScore,
      });

      // ── Novel dimensions computation (Phase 3) ──
      // Color Harmony — uses k-means palette from singlePassAnalysis
      const colorHarmonyResult = analyzeColorHarmony(pa.dominantColors);
      const modeAdjustedHarmony = applyHarmonyModeWeight(colorHarmonyResult, options.modeId ?? 'balanced');

      // Depth Perception — proxy from Sobel gradient analysis
      const thirdH = Math.floor(h / 3);
      let topEdgeSum = 0, topCount = 0, bottomEdgeSum = 0, bottomCount = 0;
      let topLumSum = 0, bottomLumSum = 0;
      let topEdgeDensity = 0, bottomEdgeDensity = 0;
      for (let y = 0; y < thirdH; y++) {
        for (let x = 0; x < w; x++) {
          const idx = y * w + x;
          topEdgeSum += pa.sobelMag[idx]; topLumSum += pa.lumArray[idx]; topCount++;
          if (pa.sobelMag[idx] > 20) topEdgeDensity++;
        }
      }
      for (let y = h - thirdH; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const idx = y * w + x;
          bottomEdgeSum += pa.sobelMag[idx]; bottomLumSum += pa.lumArray[idx]; bottomCount++;
          if (pa.sobelMag[idx] > 20) bottomEdgeDensity++;
        }
      }
      const avgTopEdge = topCount > 0 ? topEdgeSum / topCount : 0;
      const avgBottomEdge = bottomCount > 0 ? bottomEdgeSum / bottomCount : 0;
      const avgTopLum = topCount > 0 ? topLumSum / topCount : 128;
      const avgBottomLum = bottomCount > 0 ? bottomLumSum / bottomCount : 128;
      const depthCues: DepthCues = {
        blurGradient: Math.min(1, Math.abs(avgBottomEdge - avgTopEdge) / 30),
        atmosphericPerspective: Math.min(1, Math.max(0, (avgTopLum - avgBottomLum) / 80)),
        sizeConstancy: 0.5, // neutral — requires object detection
        overlappingForms: 0.5, // neutral — requires segmentation
        foregroundPresence: bottomCount > 0 ? Math.min(1, (bottomEdgeDensity / bottomCount) / 0.15) : 0,
        textureGradient: topCount > 0 && bottomCount > 0 ? Math.min(1, Math.abs((topEdgeDensity / topCount) - (bottomEdgeDensity / bottomCount)) / 0.1) : 0,
      };
      const depthScore = analyzeDepth(depthCues);

      // Authenticity Index — from Phase 1 detectors
      const authenticityResult = analyzeAuthenticity({
        hdrArtifacts: hdrDetector.severity,
        oversaturation: oversatDetector.severity,
        noisePatterns: noiseDetector.severity,
        filterDetected: false,
        compressionArtifacts: 0,
        unnaturalSharpening: oversharpenDetector.severity,
      });

      // Emotional Resonance — from lighting + composition + face signals
      const colorTemp: 'warm' | 'cool' | 'neutral' =
        lightingResult.kelvin <= 4500 ? 'warm' : lightingResult.kelvin >= 7000 ? 'cool' : 'neutral';
      const contrastLevel = Math.min(1, lightingResult.contrastStdDev / 70);
      const expressionValence = faceResult.faceCount > 0 ? faceResult.smileIntensity * 2 - 1 : 0;
      const expressionIntensity = faceResult.faceCount > 0 ? faceResult.smileIntensity : 0;
      const emotionalResult = analyzeEmotionalResonance(
        colorTemp, contrastLevel, expressionValence, expressionIntensity,
        compositionResult.leadingLinesScore, compositionResult.negativeSpaceRatio,
      );

      // Gaze dynamics — from MediaPipe iris landmarks (patent: novel dimension)
      let gazeScore: number | null = null;
      const gazeResult = computeGazeFromLandmarks(
        faceResult.faceLandmarks, faceResult.faceCount, faceResult.eyeContact,
      );
      if (gazeResult) {
        gazeScore = analyzeGazeDynamics(gazeResult);
      }

      // Subject-environment harmony — from face bbox color separation (patent: novel dimension)
      let subjectEnvScore: number | null = null;
      const subjectEnvSignals = computeSubjectEnvironmentSignals(
        faceResult.faceLandmarks, faceResult.faceCount, imageData.data, w, h,
      );
      if (subjectEnvSignals) {
        subjectEnvScore = analyzeSubjectEnvironment(
          subjectEnvSignals.subjectHSV, subjectEnvSignals.bgHSV,
          subjectEnvSignals.faceArea, faceResult.faceCount,
        );
      }

      const novelDimensions: NovelDimensionScores = {
        colorHarmony: modeAdjustedHarmony,
        depthPerception: depthScore,
        authenticityIndex: authenticityResult.score,
        subjectEnvironment: subjectEnvScore,
        gazeDynamics: gazeScore,
        emotionalResonance: emotionalResult.score,
        temporalPeak: null, // requires burst context (future)
      };

      // Apply vibe parser novel hints to mode weights if present
      let modeWeights = { ...(NOVEL_MODE_WEIGHTS[options.modeId ?? 'balanced'] ?? NOVEL_MODE_WEIGHTS.balanced) };
      if (options.novelHints && Object.keys(options.novelHints).length > 0) {
        for (const [key, delta] of Object.entries(options.novelHints)) {
          if (key in modeWeights && key !== 'novelBlendRatio') {
            (modeWeights as any)[key] = Math.max(0, ((modeWeights as any)[key] ?? 0) + (delta ?? 0));
          }
        }
        // Renormalize dimension weights (excluding novelBlendRatio)
        const { novelBlendRatio, ...dims } = modeWeights;
        const total = Object.values(dims).reduce((s, v) => s + (v as number), 0);
        if (total > 0) {
          for (const k of Object.keys(dims)) {
            (modeWeights as any)[k] = (modeWeights as any)[k] / total;
          }
        }
      }
      const novelComposite = computeNovelComposite(novelDimensions, modeWeights);
      const blendedComposite = blendComposites(frameScore, novelComposite, modeWeights.novelBlendRatio);

      // Add novel dimension notes
      if (colorHarmonyResult.relationship) notes._colorHarmony = `${colorHarmonyResult.relationship} palette (${modeAdjustedHarmony.toFixed(1)}/10)`;
      if (authenticityResult.score < 6) notes._authenticity = `Processing detected — authenticity ${authenticityResult.score.toFixed(1)}/10`;
      if (emotionalResult.colorTemperatureEmotion) notes._emotionalResonance = emotionalResult.colorTemperatureEmotion;

      // Detect descriptive photo tags (moved before social calibration which needs them)
      const photoTags = detectPhotoTags(
        faceResult, pa, lightingResult, w, h,
      );

      // ── Social Calibration (mode-specific context dimensions) ──
      const socialSignals: SocialSignals = {
        faceCount: faceResult.faceCount,
        primaryFaceConfidence: faceResult.largestFaceRatio > 0 ? 0.9 : 0,
        smileIntensity: faceResult.smileIntensity,
        eyeContactStrength: faceResult.eyeContact,
        bodyLanguageOpenness: 0.5,
        expressionGenuineness: faceResult.naturalness,
        sceneCategory: inferSceneCategory(photoTags),
        sceneEnergy: Math.min(1, pa.colorfulness + compositionResult.leadingLinesScore * 0.3),
        settingAspirationLevel: 0.5,
        hasAnimal: photoTags.includes('pet'),
        hasFood: photoTags.includes('food'),
        colorVibrancy: pa.colorfulness,
        contrastLevel: Math.min(1, lightingResult.contrastStdDev / 70),
        visualComplexity: compositionResult.bgComplexity,
        uniquenessScore: 0.5,
        dominantColorWarmth: Math.min(1, Math.max(0, (lightingResult.warmthRatio - 0.6) / 1.2)),
        groomingScore: 0.5,
        backgroundCleanness: 1 - compositionResult.bgComplexity,
        outfitCoherence: 0.5,
        subjectProminence: faceResult.largestFaceRatio,
        atmosphericDepth: depthScore / 10,
        lightingMood: lightingResult.isGoldenHour ? 'warm' as const :
          lightingResult.kelvin < 4000 ? 'warm' as const :
          lightingResult.kelvin > 7000 ? 'cool' as const : 'neutral' as const,
        goldenHourDetected: lightingResult.isGoldenHour,
        photoIndex: i,
        totalPhotos: imageFiles.length,
      };

      allSocialSignals.push(socialSignals);

      const socialCalibrationScores = scoreSocialCalibration(
        socialSignals, options.modeId || 'balanced',
      );
      const socialCalibrationComposite = computeSocialCalibrationComposite(
        socialCalibrationScores, options.modeId || 'balanced',
      );

      let socialBlendedScore = blendedComposite;
      if (socialCalibrationComposite > 0) {
        socialBlendedScore = blendWithSocialCalibration(
          blendedComposite, socialCalibrationComposite, options.modeId || 'balanced',
        );
      }

      // PRISM: Apply personalization modifier if enabled and confident
      let effectiveComposite = socialBlendedScore;
      let prismAdjustment = undefined;
      if (options.prismState?.enabled && options.prismState.preferenceVector.confidence > 0.1) {
        prismAdjustment = applyPrismModifier(
          frameScore,
          dimensions,
          prismAesthetics,
          options.prismState.ownerBaseline,
          options.prismState.preferenceVector,
        );
        effectiveComposite = prismAdjustment.personalizedComposite;
      }

      // Debug overlay: expose intermediate values when options.debug is true
      const _debug: ScoringDebug | undefined = options.debug ? {
        rawDimensions: {
          lighting: lightingResult.score, composition: compositionResult.score,
          sceneInterest: sceneResult.score, portrait: faceResult.faceCount > 0 ? faceResult.score : null,
        },
        calibratedDimensions: {
          lighting: lightingRaw, composition: compositionRaw,
          sceneInterest: sceneRaw, portrait: portraitRaw,
        },
        effectiveWeights: {
          portrait: effectiveWeights.portraitWeight, lighting: effectiveWeights.lightingWeight,
          composition: effectiveWeights.compositionWeight, scene: effectiveWeights.sceneWeight,
        },
        detractorChain: detractorNotes.map(n => {
          const [type, pts] = n.split(': -');
          return { type: type.trim(), severity: parseFloat(pts), remaining: 0 };
        }),
        novelInputs: {
          depthCues: depthCues as unknown as Record<string, number>,
          authenticitySignals: {
            hdrArtifacts: hdrDetector.severity,
            oversaturation: oversatDetector.severity,
            noisePatterns: noiseDetector.severity,
            unnaturalSharpening: oversharpenDetector.severity,
          },
          colorTemp,
        },
        exifBonuses: { bonus: exifContext.bonus, notes: exifContext.notes },
        modeSpecificBonuses,
      } : undefined;

      // Smart mode suggestion — detect mode-photo mismatch
      const modeSuggestion = suggestBetterMode(
        options.modeId || 'balanced',
        faceResult.faceCount,
        faceResult.isSelfie,
        dimensions.portraitComposition,
        dimensions.sceneInterest,
        detectedLandmarkTier,
      );

      // v2 Glow Score — capability-based optimistic ceiling (shared implementation)
      const glowResult = isPortrait ? computeGlowScore(
        { portrait: portraitRaw, lighting: lightingRaw, composition: compositionRaw, scene: sceneRaw },
        rawComposite, totalDetractorPenalty, frameScore, effectiveWeights,
      ) : undefined;
      const glowScore = glowResult?.glowScore;
      const glowDelta = glowResult?.delta ?? 0;
      const glowExplanation = glowResult?.explanation;

      // v2: Affirmation-first diagnostic card
      const diagnosticV2 = isPortrait ? generateDiagnosticV2(
        { portrait: portraitRaw, lighting: lightingRaw, composition: compositionRaw, scene: sceneRaw },
        frameScore,
      ) : undefined;
      if (diagnosticV2) {
        notes._diagnosticV2 = JSON.stringify(diagnosticV2);
      }

      scores.push({
        id: `web_${i}`,
        assetIdentifier: file.name,
        dimensions,
        landmarkTier: detectedLandmarkTier,
        detractorPenalty: totalDetractorPenalty,
        detectedFaceCount: faceResult.faceCount,
        rawCompositeScore: rawComposite,
        compositeScore: effectiveComposite,
        potentialScore: glowScore,
        glowScore,
        glowExplanation,
        dimensionNotes: notes,
        isSelected: false,
        processingDurationMs: performance.now() - start,
        thumbnailUri: thumbnailUrl,
        prismAesthetics,
        prismAdjustment,
        novelDimensions,
        novelCompositeScore: novelComposite,
        blendedCompositeScore: socialBlendedScore,
        socialCalibration: socialCalibrationComposite > 0 ? socialCalibrationScores : undefined,
        socialCalibrationComposite: socialCalibrationComposite > 0 ? socialCalibrationComposite : undefined,
        photoCategory: (nonPhotoCheck.isNonPhoto && nonPhotoCheck.category !== 'photo'
          ? nonPhotoCheck.category : 'genuine') as PhotoCategory,
        photoTags: photoTags.length > 0 ? photoTags : undefined,
        classificationConfidence: nonPhotoCheck.isNonPhoto
          ? nonPhotoCheck.confidence : 1.0 - pa.screenshotConfidence,
        modeSuggestion,
        ...(_debug ? { _debug } : {}),
      });

      hashMap.push({ id: `web_${i}`, hash: imgHash, compositeScore: effectiveComposite });

    } catch (err) {
      console.error(`[FRAME] Error scoring ${file.name}:`, err);
      scores.push({
        id: `web_${i}`,
        assetIdentifier: file.name,
        dimensions: { portraitComposition: null, lighting: 0, composition: 0, sceneInterest: 0, sceneInterestSpiked: 0 },
        landmarkTier: 0 as LandmarkTier,
        detractorPenalty: 0,
        detectedFaceCount: 0,
        rawCompositeScore: 0,
        compositeScore: 0,
        dimensionNotes: { _error: 'Failed to process' },
        isSelected: false,
        processingDurationMs: performance.now() - start,
        thumbnailUri: (() => { const u = URL.createObjectURL(file); blobUrls.push(u); return u; })(),
      });
    } finally {
      if (canvas) { canvas.width = 1; canvas.height = 1; canvas = null; }
      await new Promise(r => setTimeout(r, 30));
    }

    options.onProgress?.(i + 1, imageFiles.length);
  }

  // Session-level social calibration: re-score D5/I3 with full session context
  if (['social', 'instagram'].includes(options.modeId || '') && allSocialSignals.length > 1) {
    for (let i = 0; i < scores.length; i++) {
      const updatedSocial = scoreSocialCalibration(
        allSocialSignals[i],
        options.modeId || 'balanced',
        allSocialSignals,
      );
      scores[i].socialCalibration = updatedSocial;
      const updatedComposite = computeSocialCalibrationComposite(
        updatedSocial, options.modeId || 'balanced',
      );
      if (updatedComposite > 0) {
        scores[i].socialCalibrationComposite = updatedComposite;
        scores[i].compositeScore = blendWithSocialCalibration(
          scores[i].blendedCompositeScore ?? scores[i].compositeScore,
          updatedComposite,
          options.modeId || 'balanced',
        );
      }
    }
  }

  scores.sort((a, b) => b.compositeScore - a.compositeScore);

  // Near-duplicate detection
  const sortedHashes = hashMap.sort((a, b) => b.compositeScore - a.compositeScore);
  const dupeMap = detectNearDuplicates(sortedHashes);
  for (const score of scores) {
    const bestId = dupeMap.get(score.id);
    if (bestId) {
      score.duplicateGroupId = bestId;
      const bestScore = scores.find(s => s.id === bestId);
      if (bestScore) {
        score.dimensionNotes._duplicate = `Similar to ${bestScore.assetIdentifier}`;
      }
    }
  }

  // Temporal Peak — assign scores to near-duplicate groups (pseudo-bursts)
  // Best photo in each group gets high temporal peak, others get descending scores
  const groupMap = new Map<string, PhotoScore[]>(); // groupLeaderId -> [members including leader]
  for (const [dupeId, leaderId] of dupeMap) {
    if (!groupMap.has(leaderId)) {
      const leader = scores.find(s => s.id === leaderId);
      if (leader) groupMap.set(leaderId, [leader]);
    }
    const member = scores.find(s => s.id === dupeId);
    if (member) groupMap.get(leaderId)?.push(member);
  }
  for (const [, group] of groupMap) {
    if (group.length < 2) continue;
    // Rank by quality: composite score (already accounts for expression, sharpness, detractors)
    const ranked = [...group].sort((a, b) => b.compositeScore - a.compositeScore);
    for (let j = 0; j < ranked.length; j++) {
      // Best photo: 8.5-9.5 based on quality gap; others descend proportionally
      const qualityRatio = ranked.length > 1
        ? (ranked.length - 1 - j) / (ranked.length - 1)
        : 1;
      const peak = j === 0
        ? Math.min(9.5, 8.0 + (ranked[0].compositeScore - (ranked[ranked.length - 1].compositeScore)) * 0.3)
        : 4.0 + qualityRatio * 4.0;
      const roundedPeak = Math.round(Math.min(10, Math.max(1, peak)) * 10) / 10;
      const nd = ranked[j].novelDimensions;
      if (nd) nd.temporalPeak = roundedPeak;
    }
  }

  const allComposites = scores.map(s => s.compositeScore);
  scores.forEach(s => {
    const label = percentileLabel(s.compositeScore, allComposites);
    if (label) s.dimensionNotes._percentile = label;
  });

  // Attach cleanup method for blob URL memory management
  (scores as any)._revokeBlobUrls = () => blobUrls.forEach(url => URL.revokeObjectURL(url));

  return scores;
}
